import { collectCursorPages } from '@app/utils/collectCursorPages';
import PagedResourceSelect from '@app/components/PagedResourceSelect';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import OrgModuleLayout from './OrgModuleLayout';
import OrgModulePlaceholder from './OrgModulePlaceholder';
import ListWorkspace, { type ListWorkspaceColumn } from '@app/components/ListWorkspace';
import CopyField from '@app/components/CopyField';
import Drawer from '@app/components/Drawer';
import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal';
import StatusToast, { type StatusToastTone } from '@app/components/StatusToast';
import Tabs, { type TabItem } from '@app/components/Tabs';
import { EyeIcon, ForkIcon, SaveIcon, TerminalIcon } from '@app/components/icons';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useApiConfiguration } from '@app/hooks/useApiConfiguration';
import { useApiQuery } from '@app/hooks/useApiQuery';
import { useCredentialsApi, useDevicesApi, useInventoryApi, useSignalsApi, useSitesApi } from '@app/hooks/useApis';
import { useCursorPagination } from '@app/hooks/useCursorPagination';
import {
  DEFAULT_INVENTORY_FILTERS, INVENTORY_PAGE_SIZES, readInventoryRouteState,
  withInventoryFilters, withInventoryPagination, withInventoryTab,
  type InventoryFilters, type InventoryTab
} from './inventoryRouteState';
import type { DeviceSummary } from '@app/types/org-console';
import type {
  Credential,
  CredentialsListResponse,
  Device,
  DeviceLifecycleschema,
  DeviceRunningConfigSnapshot,
  DeviceUpdate,
  DevicesListResponse,
  IntegrationConnectionCMDBBinding,
  InventoryCMDBConnection,
  SitesListResponse,
} from '@app/types/sdk';
import { ApiResponseError, buildOrgUrl, cloneHeadersWithAuth } from '@app/utils/api';
import { formatDeviceRoleLabel, isWirelessApRole, sortDeviceRoles } from '@app/utils/deviceRoles';
import { formatDeviceOS, getDeviceOsName } from '@app/utils/deviceDisplay';
import { resolveErrorMessage } from '@app/utils/errors';
import { formatDateTime, formatRelativeTime } from '@app/utils/format';
import { getConfidenceBadgeClass } from '@app/utils/confidence';
import { isUUIDString } from '@app/utils/uuid';
import {
  describeProviderEdgeEvidence,
  formatProviderEdgeConfidence,
  formatProviderEdgeLabel,
  normalizeProviderEdgePorts,
  providerEdgeClassificationTone,
  shouldDisplayProviderEdgePort,
  type ProviderEdgePortRecord
} from '@app/utils/providerEdge';
import { EndpointDetailContent, formatMacAddress, normalizeEndpointDetailResponse, type EndpointRecord } from './endpointShared';
import {
  groupSSHSessionSignals,
  SSH_CONNECTION_LIMIT_SIGNAL_KEY,
  SSH_CONNECTIONS_BY_VRF_SIGNAL_KEY,
  SSH_CONNECTIONS_TOTAL_SIGNAL_KEY,
  SSH_PER_HOST_CONNECTION_LIMIT_SIGNAL_KEY,
  type SSHSessionSignalSummary
} from './sshSessionSummary';

type DeviceFilters = InventoryFilters;

interface DeviceDrawerState {
  open: boolean;
  loading: boolean;
  saving: boolean;
  error: string | null;
  record: DeviceDetailRecord | null;
  etag: string | null;
}

interface DeviceDetailRecord extends Omit<Device, 'providerEdgePorts'> {
  lifecycle?: DeviceLifecycleschema;
  discoveryCLICredential?: string | null;
  discoverySNMPCredential?: string | null;
  resolvedSshCredential?: string | null;
  resolvedSnmpCredential?: string | null;
  type?: string;
  providerEdgePorts?: ProviderEdgePortRecord[] | null;
  attributes?: {
    discoveryCLICredential?: string | null;
    discoverySNMPCredential?: string | null;
  } | null;
}

interface DiscoveryCredentialRecord {
  credentialId: string;
  type: 'SSH' | 'SNMP';
}

interface RunningConfigPreviewState {
  loading: boolean;
  error: string | null;
  summary: string[];
  startupConfigInSync?: boolean;
}

interface DeviceSSHSessionState {
  loading: boolean;
  error: string | null;
  summary: SSHSessionSignalSummary | null;
}

interface DeviceRecentSyslogRecord {
  timestamp: string;
  message: string;
}

interface DeviceRecentSyslogsState {
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  items: DeviceRecentSyslogRecord[];
  hasMore: boolean;
}

interface DeviceRecentSyslogsResponse {
  items?: DeviceRecentSyslogRecord[];
  limit?: number;
  offset?: number;
  hasMore?: boolean;
}

interface DeviceAuditRecord {
  eventTime: string;
  collectedAt: string;
  username: string;
  tty: string;
  remoteAddr: string;
  action: string;
  attributesRaw: string;
  commandText: string;
  rawRecord: string;
  sourceReqId: string;
}

interface DeviceAuditRecordsState {
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  items: DeviceAuditRecord[];
  hasMore: boolean;
}

interface DeviceAuditRecordsResponse {
  items?: DeviceAuditRecord[];
  limit?: number;
  offset?: number;
  hasMore?: boolean;
}

interface DeviceAttachedEndpointRecord {
  id: string;
  endpointKey: string;
  siteId: string;
  macAddress: string;
  ipAddress: string;
  switchDeviceId: string;
  switchPort: string;
  lastSeenAt: string;
  updatedAt: string;
  attrs: Record<string, unknown> | null;
}

interface DeviceAttachedEndpointsState {
  loading: boolean;
  error: string | null;
  items: DeviceAttachedEndpointRecord[];
  exactTotal: number | null;
}

interface AttachedEndpointDrawerState {
  open: boolean;
  loading: boolean;
  error: string | null;
  endpointId: string | null;
  record: EndpointRecord | null;
}

interface DeviceListResponseItem extends Device {
  runningConfigStatus?: {
    startupConfigInSync?: boolean;
  };
}

interface InventoryDevicesListResponse extends Omit<DevicesListResponse, 'items'> {
  items?: DeviceListResponseItem[];
}

interface LabelPair {
  key: string;
  value: string;
}

interface DeviceFormValues {
  displayName: string;
  role: string;
  notes: string;
  labels: LabelPair[];
}

type IntegrationConnectionRecord = InventoryCMDBConnection;

interface CMDBSiteOption {
  value: string;
  label: string;
}

const DEFAULT_FILTERS = DEFAULT_INVENTORY_FILTERS;
const PAGE_SIZE_OPTIONS = INVENTORY_PAGE_SIZES;
const DEVICE_STATES = ['discovered', 'validated', 'unreachable', 'retired', 'merged'];
const CMDB_ALL_SITES = '__all__';
const CMDB_UNKNOWN_SITE = '__unknown__';
const createInitialDrawerState = (): DeviceDrawerState => ({
  open: false,
  loading: false,
  saving: false,
  error: null,
  record: null,
  etag: null
});

const createInitialRunningConfigPreviewState = (): RunningConfigPreviewState => ({
  loading: false,
  error: null,
  summary: [],
  startupConfigInSync: undefined
});

const createInitialDeviceSSHSessionState = (): DeviceSSHSessionState => ({
  loading: false,
  error: null,
  summary: null
});

const formatSSHSessionCount = (value: number | undefined): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '—';
  }
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
};

const createInitialRecentDeviceSyslogsState = (): DeviceRecentSyslogsState => ({
  loading: false,
  loadingMore: false,
  error: null,
  items: [],
  hasMore: false
});

const createInitialDeviceAuditRecordsState = (): DeviceAuditRecordsState => ({
  loading: false,
  loadingMore: false,
  error: null,
  items: [],
  hasMore: false
});

const createInitialAttachedEndpointsState = (): DeviceAttachedEndpointsState => ({
  loading: false,
  error: null,
  items: [],
  exactTotal: null
});

const createInitialAttachedEndpointDrawerState = (): AttachedEndpointDrawerState => ({
  open: false,
  loading: false,
  error: null,
  endpointId: null,
  record: null
});

const normalizeCMDBBindingStatus = (value?: string | null): string => (value ?? '').trim().toLowerCase();

const toLabelPairs = (labels?: Record<string, string> | null): LabelPair[] => {
  if (!labels) {
    return [];
  }
  return Object.entries(labels).map(([key, value]) => ({ key, value: String(value ?? '') }));
};

const labelPairsToRecord = (pairs: LabelPair[]): Record<string, string> => {
  return pairs.reduce<Record<string, string>>((acc, pair) => {
    const trimmedKey = pair.key.trim();
    if (!trimmedKey) {
      return acc;
    }
    acc[trimmedKey] = pair.value.trim();
    return acc;
  }, {});
};

const areLabelRecordsEqual = (a: Record<string, string>, b: Record<string, string>): boolean => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) {
    return false;
  }
  return aKeys.every((key) => a[key] === b[key]);
};

const readString = (value: unknown): string => (typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim());

const readObject = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

const readNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(readString(value));
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeAttachedEndpointRecord = (raw: unknown): DeviceAttachedEndpointRecord | null => {
  const record = readObject(raw);
  if (!record) {
    return null;
  }
  const endpointKey = readString(record.endpointKey ?? record.endpoint_key ?? record.macAddress ?? record.mac_address);
  const macAddress = readString(record.macAddress ?? record.mac_address ?? endpointKey);
  if (!endpointKey || !macAddress) {
    return null;
  }
  return {
    id: readString(record.id ?? endpointKey) || endpointKey,
    endpointKey,
    siteId: readString(record.siteId ?? record.site_id),
    macAddress,
    ipAddress: readString(record.ipAddress ?? record.ip_address),
    switchDeviceId: readString(record.switchDeviceId ?? record.switch_device_id),
    switchPort: readString(record.switchPort ?? record.switch_port),
    lastSeenAt: readString(record.lastSeenAt ?? record.last_seen_at),
    updatedAt: readString(record.updatedAt ?? record.updated_at),
    attrs: readObject(record.attrs)
  };
};

const normalizeAttachedEndpointsResponse = (raw: unknown): { items: DeviceAttachedEndpointRecord[]; exactTotal: number | null } => {
  const payload = readObject(raw);
  const items = Array.isArray(payload?.items) ? payload.items.map(normalizeAttachedEndpointRecord).filter(Boolean) as DeviceAttachedEndpointRecord[] : [];
  const exactTotalValue = payload?.exactTotal ?? payload?.exact_total ?? payload?.totalCount ?? payload?.total_count ?? payload?.count;
  const exactTotal = exactTotalValue == null ? null : readNumber(exactTotalValue);
  return { items, exactTotal };
};

const normalizeDeviceAuditRecord = (raw: unknown): DeviceAuditRecord | null => {
  const record = readObject(raw);
  if (!record) {
    return null;
  }
  const eventTime = readString(record.eventTime ?? record.event_time);
  const collectedAt = readString(record.collectedAt ?? record.collected_at);
  const username = readString(record.username);
  const commandText = readString(record.commandText ?? record.command_text);
  const rawRecord = readString(record.rawRecord ?? record.raw_record);
  if (!eventTime && !collectedAt && !username && !commandText && !rawRecord) {
    return null;
  }
  return {
    eventTime,
    collectedAt,
    username,
    tty: readString(record.tty),
    remoteAddr: readString(record.remoteAddr ?? record.remote_addr),
    action: readString(record.action),
    attributesRaw: readString(record.attributesRaw ?? record.attributes_raw),
    commandText,
    rawRecord,
    sourceReqId: readString(record.sourceReqId ?? record.source_req_id)
  };
};

const normalizeDeviceAuditRecordsResponse = (raw: unknown): DeviceAuditRecordsResponse => {
  const payload = readObject(raw);
  const items = Array.isArray(payload?.items) ? payload.items.map(normalizeDeviceAuditRecord).filter(Boolean) as DeviceAuditRecord[] : [];
  return {
    items,
    limit: payload?.limit == null ? undefined : readNumber(payload.limit),
    offset: payload?.offset == null ? undefined : readNumber(payload.offset),
    hasMore: payload?.hasMore === true || payload?.has_more === true
  };
};

const readNestedString = (root: Record<string, unknown> | null | undefined, key: string): string => {
  if (!root) {
    return '';
  }
  return readString(root[key]);
};

const formatBadgeClass = (tone: 'success' | 'warning' | 'danger' | 'ghost') => `ui-badge ui-badge--${tone}`;
const formatLifecycleStatusLabel = (status?: string | null): string => {
  switch (status) {
    case 'supported':
      return 'Supported';
    case 'end_of_sale':
      return 'End of sale';
    case 'end_of_support':
      return 'End of support';
    case 'end_of_life':
      return 'End of life';
    case 'unknown':
    default:
      return 'Unknown';
  }
};

const formatLifecycleLookupLabel = (status?: string | null): string => {
  switch (status) {
    case 'pending':
      return 'Lookup pending';
    case 'found':
      return 'Official source found';
    case 'not_found':
      return 'Not found';
    case 'failed':
      return 'Lookup failed';
    default:
      return 'Unavailable';
  }
};

const getLifecycleBadgeClass = (lifecycle?: DeviceLifecycleschema | null): string => {
  if (!lifecycle) {
    return 'ui-badge ui-badge--ghost';
  }
  if (lifecycle.lookupStatus === 'failed' || lifecycle.lifecycleStatus === 'end_of_life') {
    return 'ui-badge ui-badge--danger';
  }
  if (lifecycle.lookupStatus === 'pending' || lifecycle.lifecycleStatus === 'end_of_sale' || lifecycle.lifecycleStatus === 'end_of_support') {
    return 'ui-badge ui-badge--warning';
  }
  if (lifecycle.lookupStatus === 'found' && lifecycle.lifecycleStatus === 'supported') {
    return 'ui-badge ui-badge--success';
  }
  return 'ui-badge ui-badge--ghost';
};

const getLifecycleSummary = (lifecycle?: DeviceLifecycleschema | null): string => {
  if (!lifecycle) {
    return 'No lifecycle record matched this device yet.';
  }
  if (lifecycle.lookupStatus === 'pending') {
    return 'Lifecycle lookup is queued or still running.';
  }
  if (lifecycle.lookupStatus === 'not_found') {
    return 'No official vendor lifecycle source has been matched yet.';
  }
  if (lifecycle.lookupStatus === 'failed') {
    return 'The latest lifecycle lookup failed and will need to be retried.';
  }
  return 'Lifecycle dates are based on the matched official vendor source.';
};

const getSafeLifecycleSourceUrl = (value?: string | null): string | null => {
  if (!value) {
    return null;
  }
  try {
    const url = new URL(value);
    return url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
};

const renderLifecycleSourceLink = (lifecycle?: DeviceLifecycleschema | null) => {
  const sourceUrl = getSafeLifecycleSourceUrl(lifecycle?.sourceUrl);
  if (!sourceUrl) {
    return <strong>—</strong>;
  }
  return (
    <a href={sourceUrl} target="_blank" rel="noreferrer">
      Official vendor source
    </a>
  );
};
const cmdbSectionStyle: CSSProperties = {
  marginBottom: '2.5rem'
};
const formatDeviceTypeLabel = (value?: string | null): string => {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : '—';
};
const cmdbSiteFilterValue = (siteId?: string | null, siteName?: string | null): string => {
  const normalizedSiteId = siteId?.trim();
  if (normalizedSiteId) {
    return normalizedSiteId;
  }
  const normalizedSiteName = siteName?.trim();
  if (normalizedSiteName) {
    return `name:${normalizedSiteName}`;
  }
  return CMDB_UNKNOWN_SITE;
};

const InventoryPage = () => {
  const orgId = useActiveOrgId();
  const apiConfig = useApiConfiguration();
  const devicesApi = useDevicesApi();
  const credentialsApi = useCredentialsApi();
  const signalsApi = useSignalsApi();
  const sitesApi = useSitesApi();
  const inventoryApi = useInventoryApi();
  const navigate = useNavigate();
  const basePath = apiConfig.basePath ?? '';

  const [searchParams, setSearchParams] = useSearchParams();
  const routeState = useMemo(() => readInventoryRouteState(searchParams), [searchParams]);
  const filters = routeState.filters;
  const siteIdFromQuery = filters.siteId;
  const deviceIdFromQuery = searchParams.get('deviceId')?.trim() ?? '';
  const [refreshIndex, setRefreshIndex] = useState(0);
  const [pendingDeleteDevices, setPendingDeleteDevices] = useState<DeviceSummary[] | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [locallyDeletedDeviceIds, setLocallyDeletedDeviceIds] = useState<Set<string>>(() => new Set());
  const [deleteToast, setDeleteToast] = useState<{ tone: StatusToastTone; message: string } | null>(null);
  const [importBusy, setImportBusy] = useState(false);

  useEffect(() => {
    setLocallyDeletedDeviceIds(new Set());
  }, [orgId]);

  const {
    state: { page, pageSize, cursor },
    setPage,
    setPageSize,
    reset: resetPagination,
    restore: restorePagination,
    cursorForPage,
    registerPageMeta,
    deriveMeta
  } = useCursorPagination({
    initialPageSize: routeState.pageSize,
    initialPage: routeState.page,
    initialCursor: routeState.cursor,
    pageSizeOptions: PAGE_SIZE_OPTIONS
  });
  const [knownVendors, setKnownVendors] = useState<string[]>([]);
  const [knownRoles, setKnownRoles] = useState<string[]>([]);
  const activeInventoryTab = routeState.tab;
  const setActiveInventoryTab = useCallback((tab: InventoryTab) => {
    setSearchParams((previous) => withInventoryTab(previous, tab), { replace: true });
  }, [setSearchParams]);

  const paginationMatchesRoute = page === routeState.page && pageSize === routeState.pageSize &&
    cursor.direction === routeState.cursor.direction && cursor.value === routeState.cursor.value;

  useEffect(() => {
    if (!paginationMatchesRoute) {
      restorePagination({ page: routeState.page, pageSize: routeState.pageSize, cursor: routeState.cursor });
    }
  }, [paginationMatchesRoute, routeState.page, routeState.pageSize, routeState.cursor.direction, routeState.cursor.value, restorePagination]);

  const openDevicePage = useCallback((deviceId: string, section: 'ssh' | 'running-config') => {
    const returnParams = withInventoryPagination(searchParams, { page, pageSize, cursor });
    const from = `/app/inventory${returnParams.toString() ? `?${returnParams.toString()}` : ''}`;
    navigate(`/app/inventory/${encodeURIComponent(deviceId)}/${section}`, { state: { from } });
  }, [cursor, navigate, page, pageSize, searchParams]);
  const [cmdbConnections, setCMDBConnections] = useState<IntegrationConnectionRecord[]>([]);
  const [loadingCMDBConnections, setLoadingCMDBConnections] = useState(false);
  const [selectedCMDBConnectionId, setSelectedCMDBConnectionId] = useState('');
  const [cmdbError, setCMDBError] = useState<string | null>(null);
  const [cmdbBindings, setCMDBBindings] = useState<IntegrationConnectionCMDBBinding[]>([]);
  const [loadingCMDBBindings, setLoadingCMDBBindings] = useState(false);
  const [selectedCMDBSite, setSelectedCMDBSite] = useState<string>(siteIdFromQuery || CMDB_ALL_SITES);

  const devicesFetcher = useMemo(() => {
    if (!orgId || !paginationMatchesRoute) {
      return null;
    }
    return async () => {
      const params = new URLSearchParams();
      params.set('limit', String(pageSize));
      if (cursor.direction === 'after' && cursor.value) {
        params.set('after', cursor.value);
      }
      if (cursor.direction === 'before' && cursor.value) {
        params.set('before', cursor.value);
      }
      if (filters.q.trim()) {
        params.set('q', filters.q.trim());
      }
      if (filters.vendor !== 'all') {
        params.set('vendor', filters.vendor);
      }
      if (filters.state !== 'all') {
        params.set('state', filters.state);
      }
      if (filters.siteId.trim()) {
        params.set('siteId', filters.siteId.trim());
      }
      if (filters.model.trim()) {
        params.set('model', filters.model.trim());
      }
      if (filters.mgmtAddress.trim()) {
        params.set('mgmtAddress', filters.mgmtAddress.trim());
      }
      if (filters.labelKey.trim()) {
        params.append('label', `${filters.labelKey.trim()}:${filters.labelValue.trim()}`);
      }
      if (filters.role !== 'all') params.set('role', filters.role);
      params.set('sort', filters.sort === 'oldest' ? 'lastSeenAt' : '-lastSeenAt');

      const headers = await cloneHeadersWithAuth({ Accept: 'application/json' });
      const response = await fetch(buildOrgUrl(basePath, orgId, `devices?${params.toString()}`), {
        method: 'GET',
        headers,
        cache: 'no-store'
      });
      if (!response.ok) {
        throw new ApiResponseError(response, `Request failed with status ${response.status}`);
      }
      return (await response.json()) as InventoryDevicesListResponse;
    };
  }, [basePath, cursor, filters, orgId, pageSize, paginationMatchesRoute, refreshIndex]);

  const {
    data,
    loading,
    error,
    backendMissing
  } = useApiQuery<InventoryDevicesListResponse>(devicesFetcher, [devicesFetcher]);

  useEffect(() => {
    if (!paginationMatchesRoute || page <= 1 || !(error instanceof ApiResponseError) || ![400, 410].includes(error.response.status)) return;
    resetPagination();
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.delete('page');
      next.delete('after');
      next.delete('before');
      return next;
    }, { replace: true });
  }, [error, page, paginationMatchesRoute, resetPagination, setSearchParams]);
  const credentialsFetcher = useMemo(
    () =>
      orgId
        ? async () => {
            const response = await credentialsApi.orgsOrgIdCredentialsGetRaw({ orgId, limit: 200 }, { cache: 'no-store' });
            return (await response.raw.json()) as CredentialsListResponse;
          }
        : null,
    [credentialsApi, orgId]
  );
  const credentialsQuery = useApiQuery<CredentialsListResponse>(credentialsFetcher, [credentialsFetcher]);
  const credentialsById = useMemo(() => {
    const map = new Map<string, Credential>();
    for (const credential of credentialsQuery.data?.items ?? []) {
      const id = credential.id?.trim();
      if (!id) {
        continue;
      }
      map.set(id, credential);
    }
    return map;
  }, [credentialsQuery.data]);
  const sitesFetcher = useMemo(
    () => (orgId ? () => collectCursorPages<SitesListResponse["items"][number]>((after) => sitesApi.orgsOrgIdSitesGet({ orgId, limit: 200, after }, { cache: 'no-store' })) : null),
    [orgId, sitesApi]
  );
  const sitesQuery = useApiQuery<{ items: SitesListResponse["items"]; meta: { exactTotal: number } }>(sitesFetcher, [sitesFetcher]);
  const siteNames = useMemo(() => {
    const lookup: Record<string, string> = {};
    for (const site of sitesQuery.data?.items ?? []) {
      if (site.id) {
        lookup[site.id] = site.name ?? site.id;
      }
    }
    return lookup;
  }, [sitesQuery.data?.items]);
  const siteOptions = useMemo(() => {
    const options = (sitesQuery.data?.items ?? [])
      .filter((site) => site.id)
      .map((site) => ({
        id: site.id as string,
        name: site.name?.trim() || site.id!
      }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    const hasSelected = filters.siteId && options.some((option) => option.id === filters.siteId);
    if (filters.siteId && !hasSelected) {
      options.unshift({
        id: filters.siteId,
        name: siteNames[filters.siteId] ?? `Unknown site (${filters.siteId})`
      });
    }
    return options;
  }, [filters.siteId, siteNames, sitesQuery.data?.items]);
  const fetchCMDBConnections = useCallback(async () => {
    if (!orgId) {
      setCMDBConnections([]);
      return;
    }
    setLoadingCMDBConnections(true);
    try {
      const payload = await devicesApi.listInventoryCMDBConnections(
        {
          orgId
        },
        { cache: 'no-store' }
      );
      setCMDBConnections(payload.items ?? []);
    } catch (caught) {
      setCMDBError(caught instanceof Error ? caught.message : 'Failed to load CMDB connections.');
      setCMDBConnections([]);
    } finally {
      setLoadingCMDBConnections(false);
    }
  }, [devicesApi, orgId]);

  useEffect(() => {
    registerPageMeta(data?.meta ?? null);
  }, [data?.meta, registerPageMeta]);

  useEffect(() => {
    setSelectedCMDBSite(siteIdFromQuery || CMDB_ALL_SITES);
  }, [siteIdFromQuery]);

  useEffect(() => {
    void fetchCMDBConnections();
  }, [fetchCMDBConnections]);

  const cmdbConnectionOptions = useMemo(() => {
    return [...cmdbConnections]
      .sort((a, b) => (a.name ?? a.provider).localeCompare(b.name ?? b.provider, undefined, { sensitivity: 'base' }));
  }, [cmdbConnections]);

  useEffect(() => {
    if (cmdbConnectionOptions.length === 0) {
      setSelectedCMDBConnectionId('');
      return;
    }
    if (selectedCMDBConnectionId && cmdbConnectionOptions.some((item) => item.id === selectedCMDBConnectionId)) {
      return;
    }
    const requestedConnectionId = searchParams.get('connectionId')?.trim();
    const preferred = cmdbConnectionOptions.find((item) => item.id === requestedConnectionId)
      ?? cmdbConnectionOptions.find((item) => item.status === 'active' && item.id)
      ?? cmdbConnectionOptions[0];
    setSelectedCMDBConnectionId(preferred.id ?? '');
  }, [cmdbConnectionOptions, searchParams, selectedCMDBConnectionId]);

  const selectedCMDBConnection = useMemo(
    () => cmdbConnectionOptions.find((item) => item.id === selectedCMDBConnectionId) ?? null,
    [cmdbConnectionOptions, selectedCMDBConnectionId]
  );

  useEffect(() => {
    setCMDBBindings([]);
  }, [selectedCMDBConnection]);

  const rawDevices = data?.items ?? [];
  const rows = useMemo<DeviceSummary[]>(() => {
    const coerce = (value?: Date | string | null): string | undefined => {
      if (!value) {
        return undefined;
      }
      return value instanceof Date ? value.toISOString() : value;
    };
    return rawDevices
      .filter((device) => !device.id || !locallyDeletedDeviceIds.has(device.id))
      .map((device, index) => ({
        index,
        row: {
          id: device.id ?? undefined,
          siteId: device.siteId ?? undefined,
          mgmtAddress: device.mgmtAddress ?? undefined,
          displayName: device.displayName ?? undefined,
          hostname: device.hostname ?? undefined,
          vendor: device.vendor ?? undefined,
          model: device.model ?? undefined,
          osName: getDeviceOsName(device),
          osVersion: device.osVersion ?? undefined,
          serial: device.serial ?? undefined,
          role: device.role ?? undefined,
          state: device.state ?? undefined,
          firstSeenAt: coerce(device.firstSeenAt),
          lastSeenAt: coerce(device.lastSeenAt),
          startupConfigDrift: (device.runningConfigStatus?.startupConfigInSync ?? null) === false
        }
      }))
      .map((entry) => entry.row);
  }, [filters.role, locallyDeletedDeviceIds, rawDevices]);
  const paginationMeta = useMemo(() => deriveMeta(rows.length), [deriveMeta, rows.length]);
  const pageCount = paginationMeta.pageCount;

  useEffect(() => {
    if (!orgId) {
      setKnownVendors([]);
      setKnownRoles([]);
      return;
    }

    let cancelled = false;
    const loadFilterOptions = async () => {
      try {
        const facets = await devicesApi.listDeviceFacets({ orgId }, { cache: 'no-store' });
        if (cancelled) return;
        setKnownVendors(facets.vendors);
        setKnownRoles(sortDeviceRoles(facets.roles, true));
      } catch (caught) {
        if (cancelled) {
          return;
        }
        console.warn('[inventory] Failed to load org-wide filter options', caught);
        setKnownVendors([]);
        setKnownRoles([]);
      }
    };

    void loadFilterOptions();
    return () => {
      cancelled = true;
    };
  }, [devicesApi, orgId, refreshIndex]);

  const handleResetFilters = useCallback(() => {
    setSearchParams((previous) => withInventoryFilters(previous, { ...DEFAULT_FILTERS, q: filters.q }), { replace: true });
    resetPagination();
  }, [filters.q, resetPagination, setSearchParams]);

  const updateFilters = useCallback(
    (next: Partial<DeviceFilters>) => {
      setSearchParams((previous) => withInventoryFilters(previous, { ...readInventoryRouteState(previous).filters, ...next }), { replace: true });
      resetPagination();
    },
    [resetPagination, setSearchParams]
  );

  const handlePageChange = useCallback(
    (nextPage: number) => {
      if (nextPage === page || nextPage < 1 || nextPage > pageCount) {
        return;
      }
      const nextCursor = cursorForPage(nextPage);
      if (nextPage > 1 && (!nextCursor.direction || !nextCursor.value)) return;
      setPage(nextPage);
      setSearchParams((previous) => withInventoryPagination(previous, { page: nextPage, pageSize, cursor: nextCursor }), { replace: true });
    },
    [cursorForPage, page, pageCount, pageSize, setPage, setSearchParams]
  );

  const handlePageSizeChange = useCallback(
    (size: number) => {
      setPageSize(size);
      setSearchParams((previous) => withInventoryPagination(previous, { page: 1, pageSize: size, cursor: { direction: null } }), { replace: true });
    },
    [setPageSize, setSearchParams]
  );

  const fetchSelectedCMDBBindings = useCallback(
    async (connection: IntegrationConnectionRecord, siteScope: string) => {
      if (!orgId || !connection.id) {
        setCMDBBindings([]);
        return;
      }
      setLoadingCMDBBindings(true);
      try {
        const payload = await devicesApi.listInventoryCMDBBindings({
          orgId,
          connectionId: connection.id,
          siteId: isUUIDString(siteScope) ? siteScope : undefined
        });
        const items = (payload.items ?? []) as IntegrationConnectionCMDBBinding[];
        setCMDBBindings(
          siteScope === CMDB_ALL_SITES
            ? items
            : items.filter((item) => cmdbSiteFilterValue(item.siteId, item.siteName) === siteScope)
        );
      } catch (caught) {
        setCMDBError(await resolveErrorMessage(caught, 'Failed to load CMDB bindings.'));
        setCMDBBindings([]);
      } finally {
        setLoadingCMDBBindings(false);
      }
    },
    [devicesApi, orgId]
  );

  useEffect(() => {
    if (!selectedCMDBConnection) {
      return;
    }
    void fetchSelectedCMDBBindings(selectedCMDBConnection, selectedCMDBSite);
  }, [fetchSelectedCMDBBindings, selectedCMDBConnection, selectedCMDBSite]);

  const [activeDeviceId, setActiveDeviceId] = useState<string | number | null>(null);
  const attemptedQueryDeviceKeyRef = useRef<string | null>(null);

  const [deviceDrawer, setDeviceDrawer] = useState<DeviceDrawerState>(() => createInitialDrawerState());
  const [runningConfigPreview, setRunningConfigPreview] = useState<RunningConfigPreviewState>(() =>
    createInitialRunningConfigPreviewState()
  );
  const [deviceSSHSession, setDeviceSSHSession] = useState<DeviceSSHSessionState>(() => createInitialDeviceSSHSessionState());
  const [recentDeviceSyslogs, setRecentDeviceSyslogs] = useState<DeviceRecentSyslogsState>(() => createInitialRecentDeviceSyslogsState());
  const [syslogSearchInput, setSyslogSearchInput] = useState('');
  const [syslogSearchQuery, setSyslogSearchQuery] = useState('');
  const [deviceAuditRecords, setDeviceAuditRecords] = useState<DeviceAuditRecordsState>(() => createInitialDeviceAuditRecordsState());
  const [auditRecordSearchInput, setAuditRecordSearchInput] = useState('');
  const [auditRecordSearchQuery, setAuditRecordSearchQuery] = useState('');
  const [attachedEndpoints, setAttachedEndpoints] = useState<DeviceAttachedEndpointsState>(() => createInitialAttachedEndpointsState());
  const [attachedEndpointDrawer, setAttachedEndpointDrawer] = useState<AttachedEndpointDrawerState>(() => createInitialAttachedEndpointDrawerState());
  const [deviceCves, setDeviceCves] = useState<{
    loading: boolean;
    error: string | null;
    items: any[];
  }>(() => ({ loading: false, error: null, items: [] }));
  const [formValues, setFormValues] = useState<DeviceFormValues>({ displayName: '', role: '', notes: '', labels: [] });
  const [formError, setFormError] = useState<string | null>(null);
  const recentSyslogRequestRef = useRef(0);
  const auditRecordRequestRef = useRef(0);
  const attachedEndpointRequestRef = useRef(0);

  const closeDrawer = useCallback(() => {
    if (searchParams.has('deviceId')) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('deviceId');
      setSearchParams(nextParams, { replace: true });
    }
    attachedEndpointRequestRef.current += 1;
    setDeviceDrawer(createInitialDrawerState());
    setRunningConfigPreview(createInitialRunningConfigPreviewState());
    setDeviceSSHSession(createInitialDeviceSSHSessionState());
    setRecentDeviceSyslogs(createInitialRecentDeviceSyslogsState());
    setSyslogSearchInput('');
    setSyslogSearchQuery('');
    setDeviceAuditRecords(createInitialDeviceAuditRecordsState());
    setAuditRecordSearchInput('');
    setAuditRecordSearchQuery('');
    setAttachedEndpoints(createInitialAttachedEndpointsState());
    setAttachedEndpointDrawer(createInitialAttachedEndpointDrawerState());
    setDeviceCves({ loading: false, error: null, items: [] });
    setFormValues({ displayName: '', role: '', notes: '', labels: [] });
    setFormError(null);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!orgId || !deviceDrawer.record?.id) {
      setDeviceCves({ loading: false, error: null, items: [] });
      return;
    }

    let cancelled = false;
    const loadDeviceCves = async () => {
      setDeviceCves({ loading: true, error: null, items: [] });
      try {
        const params = new URLSearchParams();
        params.set('limit', '1000');
        params.set('device_id', deviceDrawer.record!.id!);
        const headers = await cloneHeadersWithAuth({ Accept: 'application/json' });
        const response = await fetch(buildOrgUrl(basePath, orgId, `cves/assessments?${params.toString()}`), {
          method: 'GET',
          headers,
          cache: 'no-store'
        });
        if (!response.ok) {
          throw new ApiResponseError(response, `Request failed with status ${response.status}`);
        }
        const payload = await response.json();
        if (cancelled) {
          return;
        }
        const items = Array.isArray(payload.items) ? payload.items : [];
        setDeviceCves({ loading: false, error: null, items });
      } catch (caught) {
        if (cancelled) {
          return;
        }
        const message = caught instanceof Error ? caught.message : 'Failed to load CVE assessments.';
        setDeviceCves({ loading: false, error: message, items: [] });
      }
    };

    void loadDeviceCves();
    return () => {
      cancelled = true;
    };
  }, [basePath, deviceDrawer.record?.id, orgId]);

  useEffect(() => {
    const deviceId = deviceDrawer.record?.id?.trim() ?? '';
    const siteId = deviceDrawer.record?.siteId?.trim() ?? '';
    if (!orgId || !deviceId || !siteId) {
      setDeviceSSHSession(createInitialDeviceSSHSessionState());
      return;
    }

    let cancelled = false;
    const loadSSHSessionSummary = async () => {
      setDeviceSSHSession({ loading: true, error: null, summary: null });
      try {
        const query = (signalKey: string) =>
          signalsApi.orgsOrgIdSignalsGet(
            {
              orgId,
              scopeType: 'site',
              scopeId: siteId,
              family: 'device_health',
              deviceId,
              signalKey,
              limit: 100
            },
            { cache: 'no-store' }
          );
        const responses = await Promise.all([
          query(SSH_CONNECTIONS_TOTAL_SIGNAL_KEY),
          query(SSH_CONNECTION_LIMIT_SIGNAL_KEY),
          query(SSH_PER_HOST_CONNECTION_LIMIT_SIGNAL_KEY),
          query(SSH_CONNECTIONS_BY_VRF_SIGNAL_KEY)
        ]);
        if (cancelled) {
          return;
        }
        const items = responses.flatMap((response) => response.items ?? []);
        const summary = groupSSHSessionSignals(items).find((item) => item.deviceId === deviceId) ?? null;
        setDeviceSSHSession({ loading: false, error: null, summary });
      } catch (caught) {
        if (cancelled) {
          return;
        }
        const message = caught instanceof Error ? caught.message : 'Failed to load SSH session telemetry.';
        setDeviceSSHSession({ loading: false, error: message, summary: null });
      }
    };

    void loadSSHSessionSummary();
    return () => {
      cancelled = true;
    };
  }, [deviceDrawer.record?.id, deviceDrawer.record?.siteId, orgId, signalsApi]);

  useEffect(() => {
    const nextQuery = syslogSearchInput.trim();
    const timer = window.setTimeout(() => {
      setSyslogSearchQuery((prev) => (prev === nextQuery ? prev : nextQuery));
    }, 250);
    return () => {
      window.clearTimeout(timer);
    };
  }, [syslogSearchInput]);

  useEffect(() => {
    const nextQuery = auditRecordSearchInput.trim();
    const timer = window.setTimeout(() => {
      setAuditRecordSearchQuery((prev) => (prev === nextQuery ? prev : nextQuery));
    }, 250);
    return () => {
      window.clearTimeout(timer);
    };
  }, [auditRecordSearchInput]);

  useEffect(() => {
    if (!deviceDrawer.record) {
      setFormValues({ displayName: '', role: '', notes: '', labels: [] });
      setFormError(null);
      return;
    }
    setFormValues({
      displayName: deviceDrawer.record.displayName ?? '',
      role: deviceDrawer.record.role ?? '',
      notes: deviceDrawer.record.notes ?? '',
      labels: toLabelPairs(deviceDrawer.record.labels)
    });
    setFormError(null);
  }, [deviceDrawer.record]);

  const loadRecentSyslogs = useCallback(
    async ({ append, offset, query }: { append: boolean; offset: number; query: string }) => {
      if (!orgId || !deviceDrawer.record?.id) {
        setRecentDeviceSyslogs(createInitialRecentDeviceSyslogsState());
        return;
      }
      const requestId = ++recentSyslogRequestRef.current;
      setRecentDeviceSyslogs((prev) =>
        append
          ? { ...prev, loadingMore: true, error: null }
          : { loading: true, loadingMore: false, error: null, items: [], hasMore: false }
      );
      try {
        const params = new URLSearchParams();
        params.set('limit', '10');
        params.set('offset', String(Math.max(0, offset)));
        if (query.trim()) {
          params.set('q', query.trim());
        }
        const headers = await cloneHeadersWithAuth({ Accept: 'application/json' });
        const response = await fetch(
          buildOrgUrl(basePath, orgId, `devices/${encodeURIComponent(deviceDrawer.record.id)}/syslogs?${params.toString()}`),
          {
            method: 'GET',
            headers,
            cache: 'no-store'
          }
        );
        if (!response.ok) {
          throw new ApiResponseError(response, `Request failed with status ${response.status}`);
        }
        const payload = (await response.json()) as DeviceRecentSyslogsResponse;
        if (requestId !== recentSyslogRequestRef.current) {
          return;
        }
        const items = Array.isArray(payload.items) ? payload.items.filter((item) => typeof item?.message === 'string') : [];
        setRecentDeviceSyslogs((prev) => ({
          loading: false,
          loadingMore: false,
          error: null,
          items: append ? [...prev.items, ...items] : items,
          hasMore: payload.hasMore === true
        }));
      } catch (caught) {
        if (requestId !== recentSyslogRequestRef.current) {
          return;
        }
        const message = caught instanceof Error ? caught.message : 'Failed to load syslogs.';
        setRecentDeviceSyslogs((prev) => ({
          loading: false,
          loadingMore: false,
          error: message,
          items: append ? prev.items : [],
          hasMore: append ? prev.hasMore : false
        }));
      }
    },
    [basePath, deviceDrawer.record?.id, orgId]
  );

  useEffect(() => {
    if (!orgId || !deviceDrawer.record?.id) {
      setRecentDeviceSyslogs(createInitialRecentDeviceSyslogsState());
      return;
    }
    void loadRecentSyslogs({ append: false, offset: 0, query: syslogSearchQuery });
  }, [deviceDrawer.record?.id, loadRecentSyslogs, orgId, syslogSearchQuery]);

  const loadDeviceAuditRecords = useCallback(
    async ({ append, offset, query }: { append: boolean; offset: number; query: string }) => {
      if (!orgId || !deviceDrawer.record?.id) {
        setDeviceAuditRecords(createInitialDeviceAuditRecordsState());
        return;
      }
      const requestId = ++auditRecordRequestRef.current;
      setDeviceAuditRecords((prev) =>
        append
          ? { ...prev, loadingMore: true, error: null }
          : { loading: true, loadingMore: false, error: null, items: [], hasMore: false }
      );
      try {
        const params = new URLSearchParams();
        params.set('limit', '10');
        params.set('offset', String(Math.max(0, offset)));
        if (query.trim()) {
          params.set('q', query.trim());
        }
        const headers = await cloneHeadersWithAuth({ Accept: 'application/json' });
        const response = await fetch(
          buildOrgUrl(basePath, orgId, `devices/${encodeURIComponent(deviceDrawer.record.id)}/audit-records?${params.toString()}`),
          {
            method: 'GET',
            headers,
            cache: 'no-store'
          }
        );
        if (!response.ok) {
          throw new ApiResponseError(response, `Request failed with status ${response.status}`);
        }
        const payload = normalizeDeviceAuditRecordsResponse(await response.json());
        if (requestId !== auditRecordRequestRef.current) {
          return;
        }
        const items = payload.items ?? [];
        setDeviceAuditRecords((prev) => ({
          loading: false,
          loadingMore: false,
          error: null,
          items: append ? [...prev.items, ...items] : items,
          hasMore: payload.hasMore === true
        }));
      } catch (caught) {
        if (requestId !== auditRecordRequestRef.current) {
          return;
        }
        const message = caught instanceof Error ? caught.message : 'Failed to load audit records.';
        setDeviceAuditRecords((prev) => ({
          loading: false,
          loadingMore: false,
          error: message,
          items: append ? prev.items : [],
          hasMore: append ? prev.hasMore : false
        }));
      }
    },
    [basePath, deviceDrawer.record?.id, orgId]
  );

  useEffect(() => {
    if (!orgId || !deviceDrawer.record?.id) {
      setDeviceAuditRecords(createInitialDeviceAuditRecordsState());
      return;
    }
    void loadDeviceAuditRecords({ append: false, offset: 0, query: auditRecordSearchQuery });
  }, [auditRecordSearchQuery, deviceDrawer.record?.id, loadDeviceAuditRecords, orgId]);

  useEffect(() => {
    if (!orgId || !deviceDrawer.record?.id) {
      attachedEndpointRequestRef.current += 1;
      setAttachedEndpoints(createInitialAttachedEndpointsState());
      setAttachedEndpointDrawer(createInitialAttachedEndpointDrawerState());
      return;
    }

    let cancelled = false;
    const loadAttachedEndpoints = async () => {
      setAttachedEndpoints((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const params = new URLSearchParams();
        params.set('limit', '50');
        params.set('sort', '-lastSeenAt');
        params.set('switchDeviceId', deviceDrawer.record!.id!);
        if (deviceDrawer.record?.siteId) {
          params.set('siteId', deviceDrawer.record.siteId);
        }
        const headers = await cloneHeadersWithAuth({ Accept: 'application/json' });
        const response = await fetch(buildOrgUrl(basePath, orgId, `endpoints?${params.toString()}`), {
          method: 'GET',
          headers,
          cache: 'no-store'
        });
        if (!response.ok) {
          throw new ApiResponseError(response, `Request failed with status ${response.status}`);
        }
        const payload = normalizeAttachedEndpointsResponse(await response.json());
        if (cancelled) {
          return;
        }
        setAttachedEndpoints({ loading: false, error: null, items: payload.items, exactTotal: payload.exactTotal });
      } catch (caught) {
        if (cancelled) {
          return;
        }
        const message = caught instanceof Error ? caught.message : 'Failed to load attached endpoints.';
        setAttachedEndpoints({ loading: false, error: message, items: [], exactTotal: null });
      }
    };

    void loadAttachedEndpoints();
    return () => {
      cancelled = true;
    };
  }, [basePath, deviceDrawer.record?.id, deviceDrawer.record?.siteId, orgId]);

  const closeAttachedEndpointDrawer = useCallback(() => {
    attachedEndpointRequestRef.current += 1;
    setAttachedEndpointDrawer(createInitialAttachedEndpointDrawerState());
  }, []);

  const openAttachedEndpointDrawer = useCallback(
    async (endpoint: DeviceAttachedEndpointRecord) => {
      if (!orgId) {
        return;
      }
      const requestId = ++attachedEndpointRequestRef.current;
      setAttachedEndpointDrawer({
        open: true,
        loading: true,
        error: null,
        endpointId: endpoint.id,
        record: null
      });
      try {
        const headers = await cloneHeadersWithAuth({ Accept: 'application/json' });
        const response = await fetch(buildOrgUrl(basePath, orgId, `endpoints/${encodeURIComponent(endpoint.id)}`), {
          method: 'GET',
          headers,
          cache: 'no-store'
        });
        if (!response.ok) {
          throw new ApiResponseError(response, `Request failed with status ${response.status}`);
        }
        const payload = normalizeEndpointDetailResponse(await response.json());
        if (!payload) {
          throw new Error(`Endpoint ${endpoint.id} was not found.`);
        }
        if (requestId !== attachedEndpointRequestRef.current) {
          return;
        }
        setAttachedEndpointDrawer({
          open: true,
          loading: false,
          error: null,
          endpointId: endpoint.id,
          record: payload
        });
      } catch (caught) {
        if (requestId !== attachedEndpointRequestRef.current) {
          return;
        }
        const message = caught instanceof Error ? caught.message : 'Failed to load endpoint details.';
        setAttachedEndpointDrawer({
          open: true,
          loading: false,
          error: message,
          endpointId: endpoint.id,
          record: null
        });
      }
    },
    [basePath, orgId]
  );

  const openAttachedEndpointPage = useCallback(() => {
    const endpointId = attachedEndpointDrawer.record?.id ?? attachedEndpointDrawer.endpointId;
    if (!endpointId) {
      return;
    }
    const params = new URLSearchParams();
    params.set('endpointId', endpointId);
    if (deviceDrawer.record?.siteId) {
      params.set('siteId', deviceDrawer.record.siteId);
    }
    if (deviceDrawer.record?.id) {
      params.set('switchDeviceId', deviceDrawer.record.id);
    }
    navigate(`/app/endpoints?${params.toString()}`);
  }, [attachedEndpointDrawer.endpointId, attachedEndpointDrawer.record?.id, deviceDrawer.record?.id, deviceDrawer.record?.siteId, navigate]);

  const attachedEndpointSummary = useMemo(
    () =>
      attachedEndpoints.items.find(
        (endpoint) => endpoint.id === attachedEndpointDrawer.endpointId || endpoint.endpointKey === attachedEndpointDrawer.endpointId
      ) ?? null,
    [attachedEndpointDrawer.endpointId, attachedEndpoints.items]
  );

  useEffect(() => {
    if (activeDeviceId == null) {
      return;
    }
    const exists = rows.some((row, index) => (row.id ?? row.mgmtAddress ?? row.displayName ?? index) === activeDeviceId);
    if (!exists) {
      setActiveDeviceId(null);
    }
  }, [activeDeviceId, rows]);

  const openDeviceDrawer = useCallback(
    (deviceId: string) => {
      if (!orgId) {
        return;
      }
      setActiveDeviceId(deviceId);
      setDeviceDrawer({ open: true, loading: true, saving: false, error: null, record: null, etag: null });
      setRunningConfigPreview({ loading: true, error: null, summary: [], startupConfigInSync: undefined });
      setRecentDeviceSyslogs(createInitialRecentDeviceSyslogsState());
      setSyslogSearchInput('');
      setSyslogSearchQuery('');
      setDeviceAuditRecords(createInitialDeviceAuditRecordsState());
      setAuditRecordSearchInput('');
      setAuditRecordSearchQuery('');
      setFormError(null);
      Promise.all([
        devicesApi.orgsOrgIdDevicesDeviceIdGetRaw({ orgId, deviceId }, { cache: 'no-store' }),
        cloneHeadersWithAuth({ Accept: 'application/json' }).then((headers) =>
          fetch(buildOrgUrl(basePath, orgId, `devices/${encodeURIComponent(deviceId)}/running-config`), {
            method: 'GET',
            headers
          })
        )
      ])
        .then(async ([deviceResponse, runningConfigResponse]) => {
          if (!deviceResponse.raw.ok) {
            throw new ApiResponseError(deviceResponse.raw, `Request failed with status ${deviceResponse.raw.status}`);
          }
          const rawPayload = await deviceResponse.raw.json();
          const payload = rawPayload as DeviceDetailRecord;
          const providerEdgePayload = rawPayload as { providerEdgePorts?: unknown; provider_edge_ports?: unknown };
          payload.providerEdgePorts = normalizeProviderEdgePorts(providerEdgePayload.providerEdgePorts ?? providerEdgePayload.provider_edge_ports);
          const etag = deviceResponse.raw.headers.get('ETag');
          setDeviceDrawer({ open: true, loading: false, saving: false, error: null, record: payload, etag: etag ?? null });

          if (runningConfigResponse.status === 404) {
            setRunningConfigPreview(createInitialRunningConfigPreviewState());
            return;
          }
          if (!runningConfigResponse.ok) {
            throw new ApiResponseError(runningConfigResponse, `Request failed with status ${runningConfigResponse.status}`);
          }
          const runningConfigPayload = (await runningConfigResponse.json()) as DeviceRunningConfigSnapshot;
          const summary = runningConfigPayload.configSummary ?? [];
          setRunningConfigPreview({
            loading: false,
            error: null,
            summary,
            startupConfigInSync: runningConfigPayload.startupConfigInSync
          });
        })
        .catch((caught: unknown) => {
          const message = caught instanceof Error ? caught.message : 'Failed to load device detail';
          setDeviceDrawer((prev) => ({ ...prev, loading: false, error: message }));
          setRunningConfigPreview({ loading: false, error: 'Failed to load running config summary.', summary: [], startupConfigInSync: undefined });
        });
    },
    [basePath, devicesApi, orgId]
  );

  const handleRowClick = useCallback(
    (row: DeviceSummary) => {
      if (!row.id) {
        return;
      }
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('deviceId', row.id);
      setSearchParams(nextParams, { replace: true });
      openDeviceDrawer(row.id);
    },
    [openDeviceDrawer, searchParams, setSearchParams]
  );

  useEffect(() => {
    const queryDeviceKey = deviceIdFromQuery && orgId ? `${orgId}:${deviceIdFromQuery}` : null;
    if (!queryDeviceKey) {
      attemptedQueryDeviceKeyRef.current = null;
      return;
    }
    if (attemptedQueryDeviceKeyRef.current === queryDeviceKey) {
      return;
    }
    if (deviceDrawer.open && (deviceDrawer.record?.id === deviceIdFromQuery || (deviceDrawer.loading && activeDeviceId === deviceIdFromQuery))) {
      attemptedQueryDeviceKeyRef.current = queryDeviceKey;
      return;
    }
    attemptedQueryDeviceKeyRef.current = queryDeviceKey;
    openDeviceDrawer(deviceIdFromQuery);
  }, [activeDeviceId, deviceDrawer.loading, deviceDrawer.open, deviceDrawer.record?.id, deviceIdFromQuery, openDeviceDrawer, orgId]);

  const refreshDevices = useCallback(() => {
    setRefreshIndex((prev) => prev + 1);
  }, []);

  const handleDeleteDevices = useCallback(
    (rowsToDelete: DeviceSummary[]) => {
      if (!orgId || rowsToDelete.length === 0 || deleteBusy) {
        return;
      }
      setPendingDeleteDevices(rowsToDelete);
    },
    [deleteBusy, orgId]
  );

  const confirmDeleteDevices = useCallback(
    async () => {
      const rowsToDelete = pendingDeleteDevices;
      if (!orgId || !rowsToDelete || rowsToDelete.length === 0 || deleteBusy) {
        return;
      }
      const deviceIds = rowsToDelete.reduce<string[]>((acc, row) => {
        if (row.id) {
          acc.push(row.id);
        }
        return acc;
      }, []);
      if (deviceIds.length === 0) {
        return;
      }

      setDeleteBusy(true);
      const results = await Promise.allSettled(
        deviceIds.map(async (deviceId) => {
          const response = await devicesApi.orgsOrgIdDevicesDeviceIdGetRaw({ orgId, deviceId }, { cache: 'no-store' });
          const etag = response.raw.headers.get('ETag') ?? undefined;
          await devicesApi.orgsOrgIdDevicesDeviceIdDelete({ orgId, deviceId, ifMatch: etag });
          return deviceId;
        })
      );
      const deletedIds = results.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));
      const failedCount = results.length - deletedIds.length;
      if (deletedIds.length > 0) {
        setLocallyDeletedDeviceIds((current) => new Set([...current, ...deletedIds]));
        setPendingDeleteDevices(null);
        if (activeDeviceId && deletedIds.includes(String(activeDeviceId))) {
          closeDrawer();
        }
        setDeleteToast({
          tone: failedCount > 0 ? 'warning' : 'success',
          message:
            failedCount > 0
              ? `${deletedIds.length} of ${results.length} devices deleted. ${failedCount} could not be deleted.`
              : `${deletedIds.length} device${deletedIds.length === 1 ? '' : 's'} deleted successfully.`
        });
        resetPagination();
        setSearchParams((previous) => withInventoryPagination(previous, {
          page: 1,
          pageSize: readInventoryRouteState(previous).pageSize,
          cursor: { direction: null }
        }), { replace: true });
        refreshDevices();
      } else {
        setDeleteToast({ tone: 'error', message: 'Failed to delete the selected devices.' });
      }
      setDeleteBusy(false);
    },
    [activeDeviceId, closeDrawer, deleteBusy, devicesApi, orgId, pendingDeleteDevices, refreshDevices, resetPagination, setSearchParams]
  );

  const handleFieldChange = useCallback((field: keyof DeviceFormValues, value: string) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleLabelsChange = useCallback((next: LabelPair[]) => {
    setFormValues((prev) => ({ ...prev, labels: next }));
  }, []);

  const handleLoadOlderSyslogs = useCallback(() => {
    if (recentDeviceSyslogs.loading || recentDeviceSyslogs.loadingMore || !recentDeviceSyslogs.hasMore) {
      return;
    }
    void loadRecentSyslogs({ append: true, offset: recentDeviceSyslogs.items.length, query: syslogSearchQuery });
  }, [loadRecentSyslogs, recentDeviceSyslogs.hasMore, recentDeviceSyslogs.items.length, recentDeviceSyslogs.loading, recentDeviceSyslogs.loadingMore, syslogSearchQuery]);

  const handleLoadOlderAuditRecords = useCallback(() => {
    if (deviceAuditRecords.loading || deviceAuditRecords.loadingMore || !deviceAuditRecords.hasMore) {
      return;
    }
    void loadDeviceAuditRecords({ append: true, offset: deviceAuditRecords.items.length, query: auditRecordSearchQuery });
  }, [
    auditRecordSearchQuery,
    deviceAuditRecords.hasMore,
    deviceAuditRecords.items.length,
    deviceAuditRecords.loading,
    deviceAuditRecords.loadingMore,
    loadDeviceAuditRecords
  ]);

  const recordLabelMap = useMemo(() => labelPairsToRecord(toLabelPairs(deviceDrawer.record?.labels)), [deviceDrawer.record]);
  const formLabelMap = useMemo(() => labelPairsToRecord(formValues.labels), [formValues.labels]);
  const normalizedDisplayName = formValues.displayName.trim();
  const normalizedRole = formValues.role.trim();
  const notesValue = formValues.notes;
  const deviceFormDirty = useMemo(() => {
    if (!deviceDrawer.record) {
      return false;
    }
    if ((deviceDrawer.record.displayName ?? '') !== normalizedDisplayName) {
      return true;
    }
    if ((deviceDrawer.record.role ?? '') !== normalizedRole) {
      return true;
    }
    if ((deviceDrawer.record.notes ?? '') !== notesValue) {
      return true;
    }
    return !areLabelRecordsEqual(recordLabelMap, formLabelMap);
  }, [deviceDrawer.record, normalizedDisplayName, normalizedRole, recordLabelMap, formLabelMap]);

  const handleSaveDevice = useCallback(async () => {
    if (!orgId || !deviceDrawer.record?.id || deviceDrawer.saving || !deviceFormDirty) {
      return;
    }
    const labelsRecord = labelPairsToRecord(formValues.labels);
    const normalizedNotes = formValues.notes;
    const payload: DeviceUpdate = {
      displayName: normalizedDisplayName || undefined,
      role: normalizedRole || undefined,
      labels: Object.keys(labelsRecord).length > 0 ? labelsRecord : undefined,
      notes: normalizedNotes
    };

    setDeviceDrawer((prev) => ({ ...prev, saving: true }));
    setFormError(null);
    try {
      const response = await devicesApi.orgsOrgIdDevicesDeviceIdPatchRaw({
        orgId,
        deviceId: deviceDrawer.record.id,
        ifMatch: deviceDrawer.etag ?? undefined,
        deviceUpdate: payload
      });
      const updatedRaw = await response.value();
      const updated = updatedRaw as DeviceDetailRecord;
      const updatedProviderEdgePayload = updatedRaw as unknown as { providerEdgePorts?: unknown; provider_edge_ports?: unknown };
      updated.providerEdgePorts = normalizeProviderEdgePorts(
        updatedProviderEdgePayload.providerEdgePorts ?? updatedProviderEdgePayload.provider_edge_ports
      );
      const nextEtag = response.raw.headers.get('ETag') ?? deviceDrawer.etag;
      setDeviceDrawer((prev) => ({ ...prev, saving: false, record: updated, etag: nextEtag ?? prev.etag ?? null, error: null }));
      refreshDevices();
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : 'Failed to save device.';
      setDeviceDrawer((prev) => ({ ...prev, saving: false }));
      setFormError(message);
    }
  }, [
    deviceDrawer.etag,
    deviceDrawer.record,
    deviceDrawer.saving,
    deviceFormDirty,
    devicesApi,
    formValues.labels,
    formValues.notes,
    normalizedDisplayName,
    normalizedRole,
    orgId,
    refreshDevices
  ]);

  const handleOpenSsh = useCallback(() => {
    if (!deviceDrawer.record?.id) {
      return;
    }
    openDevicePage(deviceDrawer.record.id, 'ssh');
  }, [deviceDrawer.record?.id, openDevicePage]);

  const handleOpenRunningConfig = useCallback(() => {
    if (!deviceDrawer.record?.id) {
      return;
    }
    openDevicePage(deviceDrawer.record.id, 'running-config');
  }, [deviceDrawer.record?.id, openDevicePage]);

  const handleOpenRunningConfigDrift = useCallback(() => {
    if (!deviceDrawer.record?.id) {
      return;
    }
    openDevicePage(deviceDrawer.record.id, 'running-config');
  }, [deviceDrawer.record?.id, openDevicePage]);

  const discoveryCredentials = useMemo(() => {
    const records: DiscoveryCredentialRecord[] = [];
    const seen = new Set<string>();
    const appendCredentials = (raw: unknown, type: DiscoveryCredentialRecord['type']) => {
      if (typeof raw !== 'string') {
        return;
      }
      const trimmed = raw.trim();
      if (!trimmed) {
        return;
      }
      const key = `${type}:${trimmed}`;
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      records.push({ credentialId: trimmed, type });
    };
    appendCredentials(deviceDrawer.record?.discoveryCLICredential, 'SSH');
    appendCredentials(deviceDrawer.record?.attributes?.discoveryCLICredential, 'SSH');
    appendCredentials(deviceDrawer.record?.discoverySNMPCredential, 'SNMP');
    appendCredentials(deviceDrawer.record?.attributes?.discoverySNMPCredential, 'SNMP');
    return records;
  }, [deviceDrawer.record]);

  const resolvedSshCredentialId = useMemo(() => {
    const raw = deviceDrawer.record?.resolvedSshCredential;
    if (typeof raw !== 'string') {
      return '';
    }
    return raw.trim();
  }, [deviceDrawer.record]);
  const resolvedSnmpCredentialId = useMemo(() => {
    const raw = deviceDrawer.record?.resolvedSnmpCredential;
    if (typeof raw !== 'string') {
      return '';
    }
    return raw.trim();
  }, [deviceDrawer.record]);

  const resolvedSshCredentialName =
    (resolvedSshCredentialId ? (credentialsById.get(resolvedSshCredentialId)?.name ?? '').trim() : '') ||
    (resolvedSshCredentialId ? 'Credential name unavailable' : '');
  const resolvedSnmpCredentialName =
    (resolvedSnmpCredentialId ? (credentialsById.get(resolvedSnmpCredentialId)?.name ?? '').trim() : '') ||
    (resolvedSnmpCredentialId ? 'Credential name unavailable' : '');
  const resolvedCredentials = useMemo(
    () =>
      [
        resolvedSshCredentialId
          ? { credentialId: resolvedSshCredentialId, name: resolvedSshCredentialName, type: 'SSH' as const }
          : null,
        resolvedSnmpCredentialId
          ? { credentialId: resolvedSnmpCredentialId, name: resolvedSnmpCredentialName, type: 'SNMP' as const }
          : null
      ].filter((entry): entry is { credentialId: string; name: string; type: 'SSH' | 'SNMP' } => entry != null),
    [resolvedSnmpCredentialId, resolvedSnmpCredentialName, resolvedSshCredentialId, resolvedSshCredentialName]
  );

  const handleOpenCredentialDetail = useCallback(
    (credentialId: string) => {
      const trimmed = credentialId.trim();
      if (!trimmed) {
        return;
      }
      navigate(`/app/credentials?tab=credentials&credentialId=${encodeURIComponent(trimmed)}`);
    },
    [navigate]
  );

  const drawerActions =
    deviceDrawer.record && !deviceDrawer.error ? (
      <div className="drawer-icon-actions">
        <button
          type="button"
          className="drawer-icon-button"
          aria-label="Open SSH session"
          title="Open SSH session"
          onClick={handleOpenSsh}
          disabled={!deviceDrawer.record?.id || deviceDrawer.loading}
        >
          <TerminalIcon size={18} />
        </button>
        <button
          type="button"
          className="drawer-icon-button"
          aria-label="Open running config"
          title="Open running config"
          onClick={handleOpenRunningConfig}
          disabled={!deviceDrawer.record?.id || deviceDrawer.loading}
        >
          <EyeIcon size={18} />
        </button>
        <button
          type="button"
          className="drawer-icon-button"
          aria-label="Save device"
          title="Save device"
          onClick={handleSaveDevice}
          disabled={!deviceFormDirty || deviceDrawer.saving || deviceDrawer.loading}
        >
          <SaveIcon size={18} />
        </button>
      </div>
    ) : undefined;

  const formLocked = deviceDrawer.loading || deviceDrawer.saving;

  const cmdbSiteScopeOptions = useMemo<CMDBSiteOption[]>(() => {
    const mapped = new Map<string, string>();
    siteOptions.forEach((site) => mapped.set(site.id, site.name));
    for (const item of cmdbBindings) {
      mapped.set(cmdbSiteFilterValue(item.siteId, item.siteName), item.siteName ?? item.siteId ?? 'Unknown site');
    }
    return [
      { value: CMDB_ALL_SITES, label: 'All sites' },
      ...Array.from(mapped.entries()).map(([value, label]) => ({ value, label }))
    ];
  }, [cmdbBindings, siteOptions]);

  const filtersActive = Boolean(
    filters.state !== 'all' ||
      filters.vendor !== 'all' ||
      filters.siteId.trim().length ||
      filters.role !== 'all' ||
      filters.model.trim().length ||
      filters.mgmtAddress.trim().length ||
      filters.labelKey.trim().length ||
      filters.labelValue.trim().length ||
      filters.sort !== 'recent'
  );

  const selectedCMDBSiteLabel =
    cmdbSiteScopeOptions.find((option) => option.value === selectedCMDBSite)?.label ??
    (selectedCMDBSite === CMDB_ALL_SITES ? 'All sites' : 'Selected site');
  const filteredCMDBBindings = cmdbBindings.filter((item) =>
    selectedCMDBSite === CMDB_ALL_SITES ? true : cmdbSiteFilterValue(item.siteId, item.siteName) === selectedCMDBSite
  );
  const cmdbAutoBindingCount = filteredCMDBBindings.filter((item) => normalizeCMDBBindingStatus(item.bindingStatus) === 'auto').length;
  const cmdbManualBindingCount = filteredCMDBBindings.filter((item) => normalizeCMDBBindingStatus(item.bindingStatus) === 'manual').length;
  const cmdbHighConfidenceBindingCount = filteredCMDBBindings.filter((item) => item.confidenceScore >= 90).length;

  const columns: ListWorkspaceColumn<DeviceSummary>[] = [
    {
      key: 'name',
      header: 'Device',
      render: (row) => row.displayName ?? row.hostname ?? row.mgmtAddress ?? '—',
      width: '20%'
    },
    {
      key: 'signals',
      header: '',
      render: (row) => (
        <div className="list-workspace__status-actions" aria-label="Device status indicators">
          {row.startupConfigDrift && !isWirelessApRole(row.role) ? (
            <button
              type="button"
              className="list-workspace__icon-button list-workspace__icon-button--signal"
              aria-label="Startup config drift detected"
              title="Startup config drift detected"
              onClick={(event) => {
                event.stopPropagation();
                if (row.id) {
                  openDevicePage(row.id, 'running-config');
                }
              }}
            >
              <ForkIcon size={16} />
            </button>
          ) : null}
        </div>
      ),
      width: '7%',
      align: 'center'
    },
    {
      key: 'mgmt',
      header: 'Mgmt address',
      render: (row) => row.mgmtAddress ?? '—',
      width: '18%'
    },
    {
      key: 'site',
      header: 'Site',
      render: (row) => (row.siteId ? siteNames[row.siteId] ?? row.siteId : '—'),
      width: '15%'
    },
    {
      key: 'role',
      header: 'Role',
      render: (row) => formatDeviceRoleLabel(row.role) || '—',
      width: '12%'
    },
    {
      key: 'vendor',
      header: 'Vendor',
      render: (row) => row.vendor ?? '—'
    },
    {
      key: 'model',
      header: 'Model',
      render: (row) => row.model ?? '—'
    },
    {
      key: 'os',
      header: 'OS',
      render: (row) => formatDeviceOS(row.osName, row.osVersion) || '—'
    },
    {
      key: 'serial',
      header: 'Serial number',
      render: (row) =>
        row.serial ? <span className="inventory-page__serial-value">{row.serial}</span> : '—'
    },
    {
      key: 'state',
      header: 'State',
      render: (row) => row.state ?? '—',
      width: '10%'
    },
    {
      key: 'lastSeen',
      header: 'Last seen',
      render: (row) => formatDateTime(row.lastSeenAt ?? null),
      width: '15%'
    }
  ];

  const rowActions = useCallback(
    (row: DeviceSummary) => {
      const id = row.id;
      if (!id || isWirelessApRole(row.role)) {
        return null;
      }
      return (
        <>
          <button
            type="button"
            className="list-workspace__icon-button"
            aria-label="Open SSH session"
            title="Open SSH session"
            onClick={(event) => {
              event.stopPropagation();
              openDevicePage(id, 'ssh');
            }}
          >
            <TerminalIcon size={16} />
          </button>
          <button
            type="button"
            className="list-workspace__icon-button"
            aria-label="Open running config"
            title="Open running config"
            onClick={(event) => {
              event.stopPropagation();
              openDevicePage(id, 'running-config');
            }}
          >
            <EyeIcon size={16} />
          </button>
        </>
      );
    },
    [openDevicePage]
  );

  const filterBar = (
    <div className="list-workspace__filter-grid">
      <div className="form-grid form-grid--two">
        <label className="form-field">
          <span className="form-field__label">Site</span>
          <PagedResourceSelect resource="sites" value={filters.siteId} allLabel="All sites" onChange={(siteId) => updateFilters({ siteId })} />
        </label>
        <label className="form-field">
          <span className="form-field__label">Vendor</span>
          <select
            className="select"
            value={filters.vendor}
            onChange={(event) => updateFilters({ vendor: event.target.value as DeviceFilters['vendor'] })}
          >
            <option value="all">All vendors</option>
            {knownVendors.map((vendor) => (
              <option key={vendor} value={vendor}>
                {vendor}
              </option>
            ))}
          </select>
        </label>
        <label className="form-field">
          <span className="form-field__label">Role/Layer</span>
          <select className="select" value={filters.role} onChange={(event) => updateFilters({ role: event.target.value })}>
            <option value="all">All roles</option>
            {knownRoles.map((role) => (
              <option key={role} value={role}>
                {formatDeviceRoleLabel(role)}
              </option>
            ))}
          </select>
        </label>
        <label className="form-field">
          <span className="form-field__label">Model</span>
          <input
            type="text"
            className="input"
            value={filters.model}
            onChange={(event) => updateFilters({ model: event.target.value })}
            placeholder="Device model"
          />
        </label>
        <label className="form-field">
          <span className="form-field__label">Mgmt address</span>
          <input
            type="text"
            className="input"
            value={filters.mgmtAddress}
            onChange={(event) => updateFilters({ mgmtAddress: event.target.value })}
            placeholder="10.0.0.1"
          />
        </label>
        <label className="form-field">
          <span className="form-field__label">State</span>
          <select className="select" value={filters.state} onChange={(event) => updateFilters({ state: event.target.value })}>
            <option value="all">All states</option>
            {DEVICE_STATES.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
        </label>
        <label className="form-field">
          <span className="form-field__label">Label key</span>
          <input
            type="text"
            className="input"
            value={filters.labelKey}
            onChange={(event) => updateFilters({ labelKey: event.target.value })}
            placeholder="env"
          />
        </label>
        <label className="form-field">
          <span className="form-field__label">Label value</span>
          <input
            type="text"
            className="input"
            value={filters.labelValue}
            onChange={(event) => updateFilters({ labelValue: event.target.value })}
            placeholder="prod"
          />
        </label>
        <label className="form-field">
          <span className="form-field__label">Sort</span>
          <select className="select" value={filters.sort} onChange={(event) => updateFilters({ sort: event.target.value as DeviceFilters['sort'] })}>
            <option value="recent">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
        </label>
      </div>
    </div>
  );

  const queueSelectedSiteImport = async () => {
    if (!orgId || !filters.siteId) return;
	const netboxSiteId = window.prompt('Enter the NetBox site ID to import (the numeric ID shown in NetBox).')?.trim();
	if (!netboxSiteId) return;
    setImportBusy(true);
    try {
      const policy = await inventoryApi.getSiteInventorySourcePolicy({ orgId, siteId: filters.siteId }, { cache: 'no-store' });
      if (policy.mode !== 'netbox_authoritative' || !policy.connectionId) throw new Error('The selected site is not configured for authoritative NetBox inventory.');
      const run = await inventoryApi.enqueueNetBoxInventoryImport({
        orgId,
        connectionId: policy.connectionId,
        inventoryImportRequest: { siteId: filters.siteId, netboxSiteId }
      }, { cache: 'no-store' });
      setDeleteToast({ tone: 'success', message: `NetBox import queued (${run.status}).` });
    } catch (error) { setDeleteToast({ tone: 'error', message: await resolveErrorMessage(error) }); }
    finally { setImportBusy(false); }
  };

  const toolbarActions = (
    <div className="inventory-page__toolbar-search">
      <label className="inventory-page__toolbar-search-field">
        <span className="sr-only">Search inventory</span>
        <input
          type="search"
          className="input"
          value={filters.q}
          onChange={(event) => updateFilters({ q: event.target.value })}
          placeholder="Search by name, vendor, model, or IP"
        />
      </label>
      {filters.q.trim().length > 0 && (
        <button type="button" className="ui-button ui-button--ghost" onClick={() => updateFilters({ q: '' })}>
          Clear
        </button>
      )}
      {filters.siteId ? <button type="button" className="shortcut-button" disabled={importBusy} onClick={() => void queueSelectedSiteImport()}>{importBusy ? 'Queueing…' : 'Import now'}</button> : null}
    </div>
  );

  const inventoryTabs = useMemo<TabItem[]>(
    () => [
      { id: 'devices', label: 'Devices' },
      {
        id: 'cmdb-bindings',
        label: 'Confirmed CMDB Mappings',
        badge: filteredCMDBBindings.length > 0 ? <span className="ui-badge">{filteredCMDBBindings.length}</span> : undefined
      }
    ],
    [filteredCMDBBindings.length]
  );

  if (!orgId) {
    return (
      <OrgModuleLayout title="Inventory" description="Org-wide device inventory">
        <OrgModulePlaceholder intent="Select an organization to load devices." />
      </OrgModuleLayout>
    );
  }

  if (backendMissing) {
    return (
      <OrgModuleLayout title="Inventory" description="Org-wide device inventory">
        <OrgModulePlaceholder intent="Implement the Devices API in the backend to load inventory." />
      </OrgModuleLayout>
    );
  }

  const cmdbBindingsContent = selectedCMDBConnection ? (
    <>
      <section style={cmdbSectionStyle}>
        <div style={{ marginBottom: '0.75rem' }}>
          <h3 style={{ margin: 0 }}>CMDB Site Scope</h3>
          <p style={{ margin: '0.35rem 0 0 0', color: 'var(--text-secondary)' }}>
            Filter confirmed device-to-CI mappings to one site. If inventory is already filtered by site, this defaults to that site.
          </p>
        </div>
        <label className="form-field">
          <span className="form-field__label">Site</span>
          <select className="select" value={selectedCMDBSite} onChange={(event) => setSelectedCMDBSite(event.target.value)}>
            {cmdbSiteScopeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </section>

      <section style={cmdbSectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          <div>
            <h3 style={{ margin: 0 }}>Confirmed CMDB Mappings</h3>
            <p style={{ margin: '0.35rem 0 0 0', color: 'var(--text-secondary)' }}>
              Read-only view of the REAP device-to-CI mappings for <strong>{selectedCMDBSiteLabel}</strong>. Configure sync and review uncertain matches in Integrations.
            </p>
          </div>
          <button
            type="button"
            className="shortcut-button"
            onClick={() => void fetchSelectedCMDBBindings(selectedCMDBConnection, selectedCMDBSite)}
            disabled={loadingCMDBBindings}
          >
            {loadingCMDBBindings ? 'Refreshing…' : 'Refresh Bindings'}
          </button>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '0.75rem',
            marginBottom: '0.75rem'
          }}
        >
          <div style={{ border: '1px solid var(--border-subtle)', borderRadius: '14px', padding: '0.85rem 1rem' }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Bound devices</div>
            <div style={{ fontSize: '1.35rem', fontWeight: 700, marginTop: '0.2rem' }}>{filteredCMDBBindings.length}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '0.2rem' }}>
              Already mapped from REAP to CMDB
            </div>
          </div>
          <div style={{ border: '1px solid var(--border-subtle)', borderRadius: '14px', padding: '0.85rem 1rem' }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Binding type</div>
            <div style={{ fontSize: '1.35rem', fontWeight: 700, marginTop: '0.2rem' }}>{cmdbAutoBindingCount}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '0.2rem' }}>
              Auto {cmdbAutoBindingCount} · Manual {cmdbManualBindingCount}
            </div>
          </div>
          <div style={{ border: '1px solid var(--border-subtle)', borderRadius: '14px', padding: '0.85rem 1rem' }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Match strength</div>
            <div style={{ fontSize: '1.35rem', fontWeight: 700, marginTop: '0.2rem' }}>{cmdbHighConfidenceBindingCount}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '0.2rem' }}>
              Confidence 90 or above
            </div>
          </div>
        </div>
        {loadingCMDBBindings ? (
          <p style={{ margin: 0, color: 'var(--text-secondary)' }}>Loading bound devices…</p>
        ) : filteredCMDBBindings.length === 0 ? (
          <div style={{ display: 'grid', gap: '0.35rem', color: 'var(--text-secondary)' }}>
            <p style={{ margin: 0 }}>
              {selectedCMDBSite === CMDB_ALL_SITES
                ? 'No REAP devices are currently bound to CMDB CIs.'
                : `No REAP devices in ${selectedCMDBSiteLabel} are currently bound to CMDB CIs.`}
            </p>
            {selectedCMDBSite !== CMDB_ALL_SITES ? (
              <p style={{ margin: 0 }}>
                Run CMDB synchronization and review uncertain matches from Integrations, or switch the site scope to All sites to review org-wide mappings.
              </p>
            ) : null}
          </div>
        ) : (
          <details open style={{ marginTop: '0.5rem' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
              Review current device-to-CI mappings ({filteredCMDBBindings.length})
            </summary>
            <div style={{ display: 'grid', gap: '0.75rem', marginTop: '0.75rem' }}>
              {filteredCMDBBindings.map((item, index) => {
                const bindingTone = item.bindingStatus?.trim().toLowerCase() === 'manual' ? 'warning' : 'success';
                const matchMethod = item.matchMethod?.trim() || 'unknown';
                return (
                  <div
                    key={`${item.deviceId}:${item.externalCiId}`}
                    style={{
                      display: 'grid',
                      gap: '0.35rem',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: '0.75rem',
                      padding: '0.75rem'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                      <div style={{ display: 'grid', gap: '0.2rem' }}>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Row {index + 1}</div>
                        <div style={{ fontWeight: 600 }}>{item.deviceName}</div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <span className={formatBadgeClass(bindingTone)}>{item.bindingStatus || 'bound'}</span>
                        <span className={getConfidenceBadgeClass(item.confidenceScore)}>{item.confidenceScore}% confidence</span>
                      </div>
                    </div>
                    <div style={{ fontSize: '0.95rem', marginTop: '0.15rem' }}>
                      REAP device <strong>{item.deviceName}</strong> is mapped to CMDB CI <strong>{item.externalCiName ?? item.externalCiId}</strong>.
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                      {item.serial ? `serial: ${item.serial}` : 'serial: —'} · {item.siteName ?? 'Unknown site'} · {item.mgmtAddress || '—'} · role: {formatDeviceRoleLabel(item.role) || '—'}
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                      {item.hostname ? `hostname: ${item.hostname}` : 'hostname: —'}
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                      CI id: {item.externalCiId} · class: {item.externalCiClass ?? '—'}
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                      Match method: {matchMethod}
                      {item.matchedFields && item.matchedFields.length > 0 ? ` · matched fields: ${item.matchedFields.join(', ')}` : ''}
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                      Last verified: {formatDateTime(item.lastVerifiedAt ?? null)}
                    </div>
                  </div>
                );
              })}
            </div>
          </details>
        )}
      </section>
    </>
  ) : (
    <OrgModulePlaceholder intent={loadingCMDBConnections ? 'Loading CMDB-capable connections…' : 'No CMDB-capable integration connection is available for this org.'} />
  );

  const cmdbTabContent = activeInventoryTab === 'cmdb-bindings'
    ? <div className="device-drawer">{cmdbBindingsContent}</div>
    : null;


  return (
    <OrgModuleLayout title="Inventory" description="Org-wide device inventory">
      <div className="inventory-page__workspace">
        <div style={{ marginBottom: '1rem' }}>
          <Tabs items={inventoryTabs} activeId={activeInventoryTab} onChange={(id) => setActiveInventoryTab(id as InventoryTab)} />
        </div>
        {activeInventoryTab === 'devices' ? (
          <ListWorkspace
            rows={rows}
            columns={columns}
            rowId={(row, index) => row.id ?? row.mgmtAddress ?? row.displayName ?? index}
            onRowClick={handleRowClick}
            highlightRowId={activeDeviceId}
            loading={loading}
            headerStatus={error ? <div className="list-workspace__status">Failed to load devices: {error.message}</div> : undefined}
            rowActions={rowActions}
            toolbarLeadingActions={toolbarActions}
            filterBar={filterBar}
            filterActive={filtersActive}
            onFilterReset={handleResetFilters}
            page={page}
            pageSize={pageSize}
            paginationMeta={paginationMeta}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onDeleteSelected={handleDeleteDevices}
            onRefresh={refreshDevices}
            emptyState={loading ? <p>Loading devices…</p> : <p>No devices discovered yet.</p>}
          />
        ) : (
          <section className="list-workspace">
            <div className="ui-tabs__panel" style={{ padding: '1.5rem' }}>
              {cmdbError && (
                <div className="form-error" role="alert" style={{ marginBottom: '1rem' }}>
                  {cmdbError}
                </div>
              )}
              {cmdbTabContent}
            </div>
          </section>
        )}
      </div>

      <Drawer
        open={deviceDrawer.open}
        title={deviceDrawer.record?.displayName ?? deviceDrawer.record?.hostname ?? deviceDrawer.record?.mgmtAddress ?? 'Device detail'}
        onClose={closeDrawer}
        width="wide"
        actions={drawerActions}
      >
        {deviceDrawer.loading && <p>Loading device…</p>}
        {!deviceDrawer.loading && deviceDrawer.error && <OrgModulePlaceholder intent={deviceDrawer.error} />}
        {!deviceDrawer.loading && deviceDrawer.record && (
          <div className="device-drawer">
            <div className="record-summary">
              <div className="record-summary__grid">
                <div>
                  <span>Device ID</span>
                  {deviceDrawer.record.id ? (
                    <CopyField value={deviceDrawer.record.id} label="Device ID" variant="chip" copiedLabel="Device ID copied" />
                  ) : (
                    <strong>—</strong>
                  )}
                </div>
                <div>
                  <span>Mgmt address</span>
                  <strong>{deviceDrawer.record.mgmtAddress ?? '—'}</strong>
                </div>
                <div>
                  <span>Vendor</span>
                  <strong>{deviceDrawer.record.vendor ?? '—'}</strong>
                </div>
                <div>
                  <span>Model</span>
                  <strong>{deviceDrawer.record.model ?? '—'}</strong>
                </div>
                <div>
                  <span>OS</span>
                  <strong>{formatDeviceOS(getDeviceOsName(deviceDrawer.record), deviceDrawer.record.osVersion) || '—'}</strong>
                </div>
                <div>
                  <span>Serial number</span>
                  <strong>{deviceDrawer.record.serial ?? '—'}</strong>
                </div>
                <div>
                  <span>Role</span>
                  <strong>{formatDeviceRoleLabel(deviceDrawer.record.role) || '—'}</strong>
                </div>
                <div>
                  <span>Type</span>
                  <strong>{formatDeviceTypeLabel(deviceDrawer.record.type)}</strong>
                </div>
                <div>
                  <span>State</span>
                  <strong>{deviceDrawer.record.state ?? '—'}</strong>
                </div>
                <div>
                  <span>Site</span>
                  <strong>
                    {deviceDrawer.record.siteId ? siteNames[deviceDrawer.record.siteId] ?? deviceDrawer.record.siteId : '—'}
                  </strong>
                </div>
                <div>
                  <span>Last seen</span>
                  <strong>{formatDateTime(deviceDrawer.record.lastSeenAt ?? null)}</strong>
                </div>
              </div>
            </div>

            <section className="detail-section">
              <header>
                <h3>SSH Session Status</h3>
              </header>
              <div className="inventory-device__ssh-panel">
                {deviceSSHSession.loading ? <p className="helper-text">Loading SSH session telemetry…</p> : null}
                {!deviceSSHSession.loading && deviceSSHSession.error ? (
                  <p className="form-error" role="alert">
                    {deviceSSHSession.error}
                  </p>
                ) : null}
                {!deviceSSHSession.loading && !deviceSSHSession.error && !deviceSSHSession.summary ? (
                  <p className="helper-text">No recent SSH session telemetry is available for this device.</p>
                ) : null}
                {!deviceSSHSession.loading && deviceSSHSession.summary ? (
                  <>
                    <div className="inventory-device__ssh-grid">
                      <div>
                        <span>Total SSH Sessions</span>
                        <strong>{formatSSHSessionCount(deviceSSHSession.summary.total?.value)}</strong>
                      </div>
                      <div>
                        <span>Global SSH Limit</span>
                        <strong>{formatSSHSessionCount(deviceSSHSession.summary.globalLimit?.value)}</strong>
                      </div>
                      <div>
                        <span>Per-Source-IP Limit</span>
                        <strong>{formatSSHSessionCount(deviceSSHSession.summary.perSourceIPLimit?.value)}</strong>
                      </div>
                      <div>
                        <span>Last Observed</span>
                        <strong>{formatDateTime(deviceSSHSession.summary.observedAt ?? null)}</strong>
                      </div>
                    </div>
                    <div className="inventory-device__ssh-vrfs">
                      <span>Sessions by VRF</span>
                      {deviceSSHSession.summary.vrfs.length > 0 ? (
                        <div className="inventory-device__ssh-vrf-list">
                          {deviceSSHSession.summary.vrfs.map((vrf) => (
                            <div className="inventory-device__ssh-vrf" key={vrf.instance ?? 'unnamed'}>
                              <span>{vrf.instance || 'unnamed'}</span>
                              <strong>{formatSSHSessionCount(vrf.value)}</strong>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <strong>—</strong>
                      )}
                    </div>
                  </>
                ) : null}
              </div>
            </section>

            <section className="detail-section">
              <header className="inventory-device__lifecycle-header">
                <h3>Product Lifecycle</h3>
                <span className={getLifecycleBadgeClass(deviceDrawer.record.lifecycle)}>
                  {formatLifecycleStatusLabel(deviceDrawer.record.lifecycle?.lifecycleStatus)}
                </span>
              </header>
              <div className="inventory-device__lifecycle-panel">
                <p className="helper-text">{getLifecycleSummary(deviceDrawer.record.lifecycle)}</p>
                <div className="inventory-device__lifecycle-grid">
                  <div>
                    <span>Lookup</span>
                    <strong>{formatLifecycleLookupLabel(deviceDrawer.record.lifecycle?.lookupStatus)}</strong>
                  </div>
                  <div>
                    <span>End of sale</span>
                    <strong>{formatDateTime(deviceDrawer.record.lifecycle?.endOfSaleDate ?? null)}</strong>
                  </div>
                  <div>
                    <span>End of support</span>
                    <strong>{formatDateTime(deviceDrawer.record.lifecycle?.endOfSupportDate ?? null)}</strong>
                  </div>
                  <div>
                    <span>End of life</span>
                    <strong>{formatDateTime(deviceDrawer.record.lifecycle?.endOfLifeDate ?? null)}</strong>
                  </div>
                  <div>
                    <span>Last checked</span>
                    <strong>{formatDateTime(deviceDrawer.record.lifecycle?.lastCheckedAt ?? null)}</strong>
                  </div>
                  <div>
                    <span>Source</span>
                    {renderLifecycleSourceLink(deviceDrawer.record.lifecycle)}
                  </div>
                </div>
              </div>
            </section>

            {formError && (
              <div className="form-error" role="alert">
                {formError}
              </div>
            )}

            <section className="detail-section">
              <header>
                <h3>Properties</h3>
              </header>
              <div className="form-grid form-grid--two">
                <label className="form-field">
                  <span className="form-field__label">Display name</span>
                  <input
                    type="text"
                    className="input"
                    value={formValues.displayName}
                    onChange={(event) => handleFieldChange('displayName', event.target.value)}
                    disabled={formLocked}
                  />
                </label>
                <label className="form-field">
                  <span className="form-field__label">Role</span>
                  <input
                    type="text"
                    className="input"
                    value={formValues.role}
                    onChange={(event) => handleFieldChange('role', event.target.value)}
                    disabled={formLocked}
                  />
                </label>
                <label className="form-field">
                  <span className="form-field__label">Type</span>
                  <input type="text" className="input" value={formatDeviceTypeLabel(deviceDrawer.record.type)} disabled />
                </label>
              </div>
              <div className="inventory-device__config-grid">
                <label className="form-field inventory-device__config-notes">
                  <span className="form-field__label">Config Notes</span>
                  <textarea
                    className="textarea"
                    value={formValues.notes}
                    onChange={(event) => handleFieldChange('notes', event.target.value)}
                    disabled={formLocked}
                    placeholder="Add optional notes about this device's configuration"
                  />
                </label>
                <section className="inventory-device__config-summary">
                  <div className="inventory-device__config-summary-header">
                    <span className="form-field__label">Running config summary</span>
                    {runningConfigPreview.startupConfigInSync === false ? (
                      <button
                        type="button"
                        className="inventory-device__drift-indicator"
                        onClick={handleOpenRunningConfigDrift}
                        title="Startup drift detected. Open running config."
                      >
                        <ForkIcon size={14} />
                        <span>Startup drift</span>
                      </button>
                    ) : null}
                  </div>
                  {runningConfigPreview.loading ? (
                    <p className="helper-text">Loading running config summary…</p>
                  ) : runningConfigPreview.error ? (
                    <p className="helper-text">{runningConfigPreview.error}</p>
                  ) : runningConfigPreview.summary.length > 0 ? (
                    <ul className="inventory-device__config-summary-list">
                      {runningConfigPreview.summary.map((item, index) => (
                        <li key={`${index}-${item}`}>{item}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="helper-text">No running config summary available yet.</p>
                  )}
                </section>
              </div>
            </section>

            <div className="inventory-device__credentials-row">
              <section className="detail-section">
                <header>
                  <h3>Discovery Credentials</h3>
                </header>
                {discoveryCredentials.length === 0 ? (
                  <p className="helper-text">No discovery credential recorded for this device.</p>
                ) : (
                  <div className="inventory-device__discovery-box">
                    <div className="inventory-device__discovery-header">
                      <span>Type</span>
                      <span>Credential</span>
                    </div>
                    {discoveryCredentials.map(({ credentialId, type }) => {
                      const credential = credentialsById.get(credentialId);
                      const credentialLabel = (credential?.name ?? '').trim() || 'Credential name unavailable';
                      return (
                        <div className="inventory-device__discovery-row" key={`${type}:${credentialId}`}>
                          <span className="inventory-device__discovery-type">{type}</span>
                          <button
                            type="button"
                            className="inventory-device__discovery-link"
                            onClick={() => handleOpenCredentialDetail(credentialId)}
                          >
                            <span>{credentialLabel}</span>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                {credentialsQuery.loading && discoveryCredentials.length > 0 ? (
                  <p className="helper-text">Resolving credential names…</p>
                ) : null}
                {credentialsQuery.error && discoveryCredentials.length > 0 ? (
                  <p className="helper-text">Unable to resolve credential names right now.</p>
                ) : null}
              </section>

              <section className="detail-section">
                <header>
                  <h3>Credentials</h3>
                </header>
                {resolvedCredentials.length > 0 ? (
                  <div className="inventory-device__discovery-box">
                    <div className="inventory-device__discovery-header">
                      <span>Type</span>
                      <span>Credential</span>
                    </div>
                    {resolvedCredentials.map(({ credentialId, name, type }) => (
                      <div key={`${type}:${credentialId}`} className="inventory-device__discovery-row">
                        <span className="inventory-device__discovery-type">{type}</span>
                        <button type="button" className="inventory-device__discovery-link" onClick={() => handleOpenCredentialDetail(credentialId)}>
                          <span>{name}</span>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="helper-text">No SSH or SNMP credential mapping found for this device.</p>
                )}
                {credentialsQuery.loading && resolvedCredentials.length > 0 ? (
                  <p className="helper-text">Resolving credential names…</p>
                ) : null}
                {credentialsQuery.error && resolvedCredentials.length > 0 ? (
                  <p className="helper-text">Unable to resolve credential names right now.</p>
                ) : null}
              </section>
            </div>

            <section className="detail-section">
              <header>
                <h3>Labels</h3>
              </header>
              <LabelsEditor values={formValues.labels} onChange={handleLabelsChange} disabled={formLocked} />
            </section>

            <section className="detail-section">
              <header className="inventory-device__syslogs-toolbar">
                <h3>Syslogs</h3>
                <label className="inventory-device__syslogs-search">
                  <span className="sr-only">Search syslogs</span>
                  <input
                    type="search"
                    className="input"
                    value={syslogSearchInput}
                    onChange={(event) => setSyslogSearchInput(event.target.value)}
                    placeholder="Case-insensitive substring search"
                  />
                </label>
                {syslogSearchInput.trim().length > 0 ? (
                  <button type="button" className="ui-button ui-button--ghost" onClick={() => setSyslogSearchInput('')}>
                    Clear
                  </button>
                ) : null}
              </header>
              {recentDeviceSyslogs.loading ? (
                <p className="helper-text">Loading syslogs…</p>
              ) : recentDeviceSyslogs.error ? (
                <p className="helper-text">{recentDeviceSyslogs.error}</p>
              ) : recentDeviceSyslogs.items.length > 0 ? (
                <>
                  <div className="inventory-device__syslogs-box">
                    <div className="inventory-device__syslogs-header">
                      <span>Seen</span>
                      <span>Message</span>
                    </div>
                    <div className="inventory-device__syslogs-scroll">
                      {recentDeviceSyslogs.items.map((syslog, index) => (
                        <div className="inventory-device__syslogs-row" key={`${syslog.timestamp}-${index}`}>
                          <div className="inventory-device__syslog-time">
                            <span>{formatRelativeTime(syslog.timestamp)}</span>
                            <small>{formatDateTime(syslog.timestamp)}</small>
                          </div>
                          <div className="inventory-device__syslog-message">{syslog.message}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {recentDeviceSyslogs.hasMore ? (
                    <div className="inventory-device__syslogs-actions">
                      <button
                        type="button"
                        className="ui-button ui-button--ghost"
                        onClick={handleLoadOlderSyslogs}
                        disabled={recentDeviceSyslogs.loadingMore}
                      >
                        {recentDeviceSyslogs.loadingMore ? 'Loading…' : 'Load older messages'}
                      </button>
                    </div>
                  ) : null}
                </>
              ) : (
                <p className="helper-text">
                  {syslogSearchQuery ? 'No syslogs match this search.' : 'No syslogs found for this device.'}
                </p>
              )}
            </section>

            <section className="detail-section">
              <header className="inventory-device__syslogs-toolbar">
                <h3>Audit Records</h3>
                <label className="inventory-device__syslogs-search">
                  <span className="sr-only">Search audit records</span>
                  <input
                    type="search"
                    className="input"
                    value={auditRecordSearchInput}
                    onChange={(event) => setAuditRecordSearchInput(event.target.value)}
                    placeholder="Search user, command, action, or attributes"
                  />
                </label>
                {auditRecordSearchInput.trim().length > 0 ? (
                  <button type="button" className="ui-button ui-button--ghost" onClick={() => setAuditRecordSearchInput('')}>
                    Clear
                  </button>
                ) : null}
              </header>
              {deviceAuditRecords.loading ? (
                <p className="helper-text">Loading audit records…</p>
              ) : deviceAuditRecords.error ? (
                <p className="helper-text">{deviceAuditRecords.error}</p>
              ) : deviceAuditRecords.items.length > 0 ? (
                <>
                  <div className="inventory-device__audit-box">
                    <div className="inventory-device__audit-header">
                      <span>Time</span>
                      <span>User</span>
                      <span>Change</span>
                    </div>
                    <div className="inventory-device__audit-scroll">
                      {deviceAuditRecords.items.map((record, index) => {
                        const effectiveTime = record.eventTime || record.collectedAt || null;
                        const userMeta = [record.tty || null, record.remoteAddr || null, record.action || null].filter(Boolean).join(' · ');
                        const command = record.commandText || record.rawRecord || record.attributesRaw || '—';
                        return (
                          <div className="inventory-device__audit-row" key={`${record.eventTime}-${record.username}-${index}`}>
                            <div className="inventory-device__audit-time">
                              <span>{formatRelativeTime(effectiveTime)}</span>
                              <small>{formatDateTime(effectiveTime)}</small>
                            </div>
                            <div className="inventory-device__audit-user">
                              <span>{record.username || '—'}</span>
                              {userMeta ? <small>{userMeta}</small> : null}
                            </div>
                            <div className="inventory-device__audit-command">
                              <span>{command}</span>
                              {record.attributesRaw && record.commandText && record.attributesRaw !== record.commandText ? (
                                <small>{record.attributesRaw}</small>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {deviceAuditRecords.hasMore ? (
                    <div className="inventory-device__syslogs-actions">
                      <button
                        type="button"
                        className="ui-button ui-button--ghost"
                        onClick={handleLoadOlderAuditRecords}
                        disabled={deviceAuditRecords.loadingMore}
                      >
                        {deviceAuditRecords.loadingMore ? 'Loading…' : 'Load older records'}
                      </button>
                    </div>
                  ) : null}
                </>
              ) : (
                <p className="helper-text">
                  {auditRecordSearchQuery ? 'No audit records match this search.' : 'No audit records found for this device.'}
                </p>
              )}
            </section>

            <section className="detail-section">
              <header>
                <h3>Provider Edge Ports</h3>
              </header>
              {(deviceDrawer.record?.providerEdgePorts?.filter(shouldDisplayProviderEdgePort).length ?? 0) > 0 ? (
                <div className="inventory-device__provider-edge-box">
                  <div className="inventory-device__provider-edge-header">
                    <span>Interface</span>
                    <span>Classification</span>
                    <span>Confidence</span>
                    <span>Evidence</span>
                  </div>
                  {deviceDrawer.record?.providerEdgePorts?.filter(shouldDisplayProviderEdgePort).map((port, index) => (
                    <div className="inventory-device__provider-edge-row" key={`${port.interfaceName ?? 'iface'}-${index}`}>
                      <div className="inventory-device__provider-edge-interface">
                        <strong>{port.interfaceName || '—'}</strong>
                      </div>
                      <div className="inventory-device__provider-edge-classification">
                        <span className={formatBadgeClass(providerEdgeClassificationTone(port.classification))}>
                          {formatProviderEdgeLabel(port.classification)}
                        </span>
                      </div>
                      <div className="inventory-device__provider-edge-confidence">
                        <span className={getConfidenceBadgeClass(port.confidence)}>{formatProviderEdgeConfidence(port.confidence)}</span>
                      </div>
                      <div className="inventory-device__provider-edge-evidence">
                        <span>{describeProviderEdgeEvidence(port)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="helper-text">No provider edge ports identified for this device.</p>
              )}
            </section>

            <section className="detail-section">
              <header>
                <h3>Attached Endpoints</h3>
              </header>
              {attachedEndpoints.loading ? (
                <p className="helper-text">Loading endpoints attached to this device…</p>
              ) : attachedEndpoints.error ? (
                <p className="helper-text">{attachedEndpoints.error}</p>
              ) : attachedEndpoints.items.length > 0 ? (
                <>
                  <p className="helper-text">Click a row to inspect the endpoint and open it in the Endpoints workspace.</p>
                  <div className="inventory-device__endpoints-box">
                    <div className="inventory-device__endpoints-header">
                      <span>Port</span>
                      <span>Endpoint</span>
                      <span>Classification</span>
                      <span>Last seen</span>
                    </div>
                    {attachedEndpoints.items.map((endpoint) => {
                      const classification = readObject(endpoint.attrs?.classification);
                      const vendor = readObject(endpoint.attrs?.vendor);
                      const classificationType = readNestedString(classification, 'type');
                      const vendorName = readNestedString(vendor, 'name');
                      return (
                        <button
                          key={endpoint.id}
                          type="button"
                          className="inventory-device__endpoints-row inventory-device__endpoints-row--interactive"
                          onClick={() => {
                            void openAttachedEndpointDrawer(endpoint);
                          }}
                        >
                          <div className="inventory-device__endpoints-port">
                            <strong>{endpoint.switchPort || '—'}</strong>
                          </div>
                          <div className="inventory-device__endpoints-main">
                            <span>{formatMacAddress(endpoint.macAddress)}</span>
                            <small>
                              {[endpoint.ipAddress || null, vendorName || null].filter(Boolean).join(' · ') || endpoint.endpointKey}
                            </small>
                          </div>
                          <div className="inventory-device__endpoints-classification">
                            <span className={formatBadgeClass(classificationType ? 'success' : 'ghost')}>
                              {classificationType || 'Unclassified'}
                            </span>
                          </div>
                          <div className="inventory-device__endpoints-seen">
                            <span>{formatRelativeTime(endpoint.lastSeenAt || endpoint.updatedAt || null)}</span>
                            <small>{formatDateTime(endpoint.lastSeenAt || endpoint.updatedAt || null)}</small>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {attachedEndpoints.exactTotal != null && attachedEndpoints.exactTotal > attachedEndpoints.items.length ? (
                    <p className="helper-text">
                      Showing {attachedEndpoints.items.length} of {attachedEndpoints.exactTotal} endpoints currently attached to this device.
                    </p>
                  ) : null}
                </>
              ) : (
                <p className="helper-text">No attached endpoints found for this device.</p>
              )}
            </section>

            <section className="detail-section">
              <header>
                <h3>CVE Assessments</h3>
              </header>
              {deviceCves.loading ? (
                <p className="helper-text">Loading CVE assessments…</p>
              ) : deviceCves.error ? (
                <p className="helper-text">{deviceCves.error}</p>
              ) : deviceCves.items.length > 0 ? (
                <div className="inventory-device__endpoints-box">
                  <div className="inventory-device__endpoints-header" style={{ gridTemplateColumns: '120px 100px 120px 1fr' }}>
                    <span>CVE ID</span>
                    <span>Severity</span>
                    <span>Status</span>
                    <span>Justification</span>
                  </div>
                  <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
                    {deviceCves.items.map((cve) => {
                      const badgeStyle = (() => {
                        const sev = (cve.severity || '').toUpperCase();
                        switch (sev) {
                          case 'CRITICAL':
                            return {
                              backgroundColor: 'rgba(239, 68, 68, 0.1)',
                              color: 'hsl(354, 85%, 48%)',
                              border: '1px solid rgba(239, 68, 68, 0.15)',
                            };
                          case 'HIGH':
                            return {
                              backgroundColor: 'rgba(245, 158, 11, 0.1)',
                              color: 'hsl(25, 90%, 45%)',
                              border: '1px solid rgba(245, 158, 11, 0.15)',
                            };
                          case 'MEDIUM':
                            return {
                              backgroundColor: 'rgba(59, 130, 246, 0.1)',
                              color: 'hsl(210, 85%, 45%)',
                              border: '1px solid rgba(59, 130, 246, 0.15)',
                            };
                          case 'LOW':
                          default:
                            return {
                              backgroundColor: 'rgba(107, 114, 128, 0.1)',
                              color: 'hsl(220, 10%, 45%)',
                              border: '1px solid rgba(107, 114, 128, 0.15)',
                            };
                        }
                      })();

                      return (
                        <div
                          key={cve.cveId}
                          className="inventory-device__endpoints-row"
                          style={{ gridTemplateColumns: '120px 100px 120px 1fr', alignItems: 'start', padding: '10px 12px' }}
                        >
                          <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{cve.cveId}</div>
                          <div>
                            <span
                              style={{
                                fontSize: '10px',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                textTransform: 'uppercase',
                                fontWeight: 'bold',
                                display: 'inline-block',
                                textAlign: 'center',
                                minWidth: '70px',
                                ...badgeStyle,
                              }}
                            >
                              {cve.severity}
                            </span>
                          </div>
                          <div>
                            <span
                              style={{
                                fontSize: '10px',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontWeight: 'bold',
                                display: 'inline-block',
                                textAlign: 'center',
                                minWidth: '90px',
                                backgroundColor: cve.status === 'EXPOSED' ? 'hsl(354, 85%, 93%)' : 'hsl(142, 70%, 93%)',
                                color: cve.status === 'EXPOSED' ? 'hsl(354, 85%, 45%)' : 'hsl(142, 75%, 25%)',
                                border: cve.status === 'EXPOSED' ? '1px solid hsl(354, 85%, 85%)' : '1px solid hsl(142, 70%, 85%)',
                              }}
                            >
                              {cve.status === 'EXPOSED' ? 'EXPOSED' : 'AUDITED SAFE'}
                            </span>
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', lineHeight: '1.4' }}>
                            {cve.justification}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="helper-text">No CVE assessments found for this device.</p>
              )}
            </section>
          </div>
        )}
      </Drawer>
      <Drawer
        open={attachedEndpointDrawer.open}
        onClose={closeAttachedEndpointDrawer}
        title={
          attachedEndpointDrawer.record?.macAddress || attachedEndpointSummary?.macAddress
            ? formatMacAddress(attachedEndpointDrawer.record?.macAddress ?? attachedEndpointSummary?.macAddress ?? '')
            : 'Endpoint'
        }
        subtitle={attachedEndpointDrawer.record?.ipAddress || attachedEndpointSummary?.ipAddress || undefined}
        footer={
          <div className="button-row">
            <button
              type="button"
              className="ui-button ui-button--primary"
              onClick={openAttachedEndpointPage}
              disabled={!attachedEndpointDrawer.endpointId}
            >
              Open Endpoint page
            </button>
          </div>
        }
      >
        {attachedEndpointDrawer.loading ? <p>Loading endpoint…</p> : null}
        {!attachedEndpointDrawer.loading && attachedEndpointDrawer.error ? <OrgModulePlaceholder intent={attachedEndpointDrawer.error} /> : null}
        {!attachedEndpointDrawer.loading && attachedEndpointDrawer.record ? <EndpointDetailContent endpoint={attachedEndpointDrawer.record} /> : null}
      </Drawer>
      <DeleteConfirmationModal
        open={pendingDeleteDevices != null}
        title={pendingDeleteDevices?.length === 1 ? 'Delete device?' : `Delete ${pendingDeleteDevices?.length ?? 0} devices?`}
        description="This permanently removes the selected devices from inventory."
        deleting={deleteBusy}
        onCancel={() => setPendingDeleteDevices(null)}
        onConfirm={() => void confirmDeleteDevices()}
      />
      {deleteToast && <StatusToast tone={deleteToast.tone} message={deleteToast.message} occurrence={deleteToast} onDismiss={() => setDeleteToast(null)} />}
    </OrgModuleLayout>
  );
};

export default InventoryPage;

interface LabelsEditorProps {
  values: LabelPair[];
  onChange: (next: LabelPair[]) => void;
  disabled?: boolean;
}

const LabelsEditor = ({ values, onChange, disabled = false }: LabelsEditorProps) => {
  const handlePairChange = (index: number, key: keyof LabelPair, value: string) => {
    const next = values.map((pair, pairIndex) => (pairIndex === index ? { ...pair, [key]: value } : pair));
    onChange(next);
  };

  const handleAdd = () => {
    onChange([...values, { key: '', value: '' }]);
  };

  const handleRemove = (index: number) => {
    onChange(values.filter((_, pairIndex) => pairIndex !== index));
  };

  return (
    <div className="labels-editor">
      {values.length === 0 && <p className="helper-text">No labels configured.</p>}
      {values.map((pair, index) => (
        <div key={`label-${index}`} className="label-row">
          <input
            type="text"
            className="input"
            placeholder="key"
            value={pair.key}
            onChange={(event) => handlePairChange(index, 'key', event.target.value)}
            disabled={disabled}
          />
          <input
            type="text"
            className="input"
            placeholder="value"
            value={pair.value}
            onChange={(event) => handlePairChange(index, 'value', event.target.value)}
            disabled={disabled}
          />
          <button type="button" className="link-button" onClick={() => handleRemove(index)} disabled={disabled}>
            Remove
          </button>
        </div>
      ))}
      <button type="button" className="link-button" onClick={handleAdd} disabled={disabled}>
        + Add label
      </button>
    </div>
  );
};
